import momentTZ from 'moment-timezone';
import momentOrig from 'moment';
import objectHash from 'object-hash';
import { createI18n } from '@/i18n';

const i18n = createI18n();

/******************************************************************************/
// Helper functions and constants                                             */
/******************************************************************************/

export const KEY_FORMATTERS = {
    day: 'YYYY-MM-DD HH',
    dayQuarterHourly: 'YYYY-MM-DD HH:mm',
    week: 'YYYY-MM-DD HH',
    month: 'YYYY-MM-DD',
    monthHourly: 'YYYY-MM-DD HH',
};

export const DISPLAY_FORMATTERS = {
    day: 'HH:mm',
    dayQuarterHourly: 'HH:mm',
    week: 'HH',
    weekTo: 'HH:mm',
    month: 'DD.MM.',
    monthHourly: 'DD.MM.',
    monthHourlyDetail: 'DD.MM. HH:mm',
};

export const UNIT_MAP = {
    day: 'hours',
    dayQuarterHourly: 'minutes',
    week: 'hours',
    month: 'days',
    monthHourly: 'hours',
};

export const INTERVAL_MAP = {
    day: 1,
    dayQuarterHourly: 15,
    week: 3,
    month: 1,
    monthHourly: 1,
};


// Math helpers for optional numbers
const sub = (a, b) => a && b ? a - b : null;
const div = (a, b) => b ? a / b : null;


export class PersonnelDeploymentHelpers {
    /**
     * Helper to parse specific dates using the timezone from store settings
     * @param args
     * @returns {moment.Moment}
     */
    moment;

    /**
     * @var storeSettings {{
     *  address: string,
     *  allowAppEmployeePreferences: boolean,
     *  allowAppVacationRequests: boolean,
     *  allowContactPersonMessage: boolean,
     *  allowFutureAssignment: boolean,
     *  allowMultiShiftAssign: boolean,
     *  appEmployeePreferencesDeadline: number,
     *  asLicExpiry: string,
     *  autoLogoutTime: number,
     *  city: string,
     *  collectiveContractRequired: boolean,
     *  companyId: number,
     *  country: number,
     *  defaultBreakTime: number,
     *  description: string,
     *  disabled: boolean,
     *  empApprovablePrefTypes: Array,
     *  employeeRulesCountMax: boolean,
     *  employeeTypeCountMax: boolean,
     *  enableAppEmployeePreferencesDeadline: boolean,
     *  enableWorkBalanceMultiplier: boolean,
     *  exitFactoring: boolean,
     *  hdWeatherLicExpiry: string,
     *  id: boolean,
     *  latitude: boolean,
     *  licExpiry: string,
     *  longitude: boolean,
     *  marketType: boolean,
     *  maxAppEmployeePreferences: boolean,
     *  maxEmployeeLevelToMp: boolean,
     *  mlLicExpiry: string,
     *  mpLicExpiry: string,
     *  mzLicExpiry: string,
     *  name: string,
     *  postalCode: string,
     *  separateOvertimeHolidayTime: boolean,
     *  shareKey: string,
     *  shiftTemplateGroupCountMax: number,
     *  simpleMode: boolean,
     *  statsLicExpiry: string,
     *  strictlyLawAbiding: boolean,
     *  timeZone: string,
     *  unapproveAppEmployeePreferences: boolean,
     *  usageStartDate: string,
     *  vacationFactoring: number,
     *  weatherLicExpiry: string,
     *  workBalanceMultiplier: number,
     *  workDays: Array<number>,
     *  workTimeFactor: number,
     *  workTimeMode: number,
     * }}
     */
    storeSettings;

    rosterHelper;
    helpers;

    constructor(storeSettings, rosterHelper, helpers, tz = null) {
        if (!tz) tz = storeSettings.timeZone;
        this.moment = (...args) => momentTZ.tz(...args, tz);
        this.storeSettings = storeSettings;
        this.rosterHelper = rosterHelper;
        this.helpers = helpers;
    }


    /******************************************************************************/
    // Getter, filter and combination functions                                   */
    /******************************************************************************/

    /**
     * Filters planning indicators based on the given parameters
     * @param indicators {Array}
     * @param from {Moment}
     * @param to {Moment}
     * @param mode {'day'|'week'|'month'|'monthHourly'}
     * @param typeId {string}
     * @param filter {'planned'|'actual'|'combined'}
     * @returns {Object}
     */
    filterIndicators(indicators, from, to, mode, typeId, filter = 'combined') {
        const indicatorMap = {};

        for (const indicator of indicators) {
            if (indicator.date) {
                indicator.from = this.moment(indicator.date).startOf(UNIT_MAP[mode]);
                indicator.to = this.moment(indicator.date).endOf(UNIT_MAP[mode]);
            }
            const indicatorFrom = this.moment(indicator.from);
            const indicatorTo = this.moment(indicator.to);

            // Check if indicator is in the given time range
            if (indicatorFrom.isBefore(from) || indicatorFrom.isSameOrAfter(to)) continue;
            // Check if indicator is of the given type, if typeId is given
            if (typeId !== indicator.typeId) continue;

            const key = indicatorFrom.format(KEY_FORMATTERS[mode]);

            if (!indicatorMap[key])
                indicatorMap[key] = {
                    from: indicatorFrom,
                    to: indicatorTo,
                    typeId,
                };

            if (indicator.planned && filter === 'actual' || !indicator.planned && filter === 'planned') continue;

            if (indicatorMap[key].from.isAfter(indicatorFrom))
                indicatorMap[key].from = indicatorFrom;
            if (indicatorMap[key].to.isBefore(indicatorTo))
                indicatorMap[key].to = indicatorTo;

            if (indicator.planned) {
                if (!indicatorMap[key].planned) indicatorMap[key].planned = [];
                if (!indicatorMap[key].plannedTotal) indicatorMap[key].plannedTotal = 0;
                indicatorMap[key].planned.push(indicator);
                indicatorMap[key].plannedTotal += indicator.sumAmount || indicator.amount || 0;
            } else {
                if (!indicatorMap[key].actual) indicatorMap[key].actual = [];
                if (!indicatorMap[key].actualTotal) indicatorMap[key].actualTotal = 0;
                indicatorMap[key].actual.push(indicator);
                indicatorMap[key].actualTotal += indicator.sumAmount || indicator.amount || 0;
            }
        }

        return indicatorMap;
    }

    /**
     * Packs the indicators into groups of given interval
     * @param indicators {Object} The indicators to pack (should be the result of filterIndicators)
     * @param mode {'day'|'week'|'month'|'monthHourly'}
     * @param interval {number} The size of the interval in the given unit
     * @returns {Object}
     */
    combineIndicators(indicators, mode, interval = 4) {
        const keyFormat = KEY_FORMATTERS[mode];
        const unit = UNIT_MAP[mode];

        const combinedIndicators = {};
        for (const [ oldKey, indicator ] of Object.entries(indicators)) {
            const indicatorDate = this.moment(oldKey, keyFormat).startOf(unit);
            // Round [unit] to the nearest interval // Note that date is the correct unit for days of month
            const valueOfUnit = unit === 'days' ? indicatorDate.get('date') : indicatorDate.get(unit);
            const rounded = Math.floor(valueOfUnit / interval) * interval;
            if (unit === 'days')
                indicatorDate.set('date', rounded + 1);
            else
                indicatorDate.set(unit, rounded);

            const key = indicatorDate.format(keyFormat);
            const indicatorFrom = this.moment(indicator.from);
            const indicatorTo = this.moment(indicator.to);

            if (!combinedIndicators[key])
                combinedIndicators[key] = {
                    from: indicatorFrom,
                    to: indicatorTo,
                    typeId: indicator.typeId,
                };

            // As the key is based on the interval, the actual range of all indicators in this group needs to be adjusted
            if (combinedIndicators[key].from.isAfter(indicatorFrom))
                combinedIndicators[key].from = indicatorFrom;
            if (combinedIndicators[key].to.isBefore(indicatorTo))
                combinedIndicators[key].to = indicatorTo;

            // Merge indicator.planned and indicator.actual, as well as indicator.plannedTotal and indicator.actualTotal
            if (indicator.planned)
                combinedIndicators[key].planned = [ ...( combinedIndicators[key].planned || [] ), ...indicator.planned ];
            if (indicator.actual)
                combinedIndicators[key].actual = [ ...( combinedIndicators[key].actual || [] ), ...indicator.actual ];
            if (indicator.plannedTotal)
                combinedIndicators[key].plannedTotal = ( combinedIndicators[key].plannedTotal || 0 ) + indicator.plannedTotal;
            if (indicator.actualTotal)
                combinedIndicators[key].actualTotal = ( combinedIndicators[key].actualTotal || 0 ) + indicator.actualTotal;
        }

        return combinedIndicators;
    }


    /**
     * Converts filtered indicator values to fractional values
     * @param indicators {Object} The indicators to convert (has to be the result of filterIndicators)
     * @param indicatorTypeMap {Object}
     * @returns {Object}
     */
    convertIndicatorsToFractions(indicators, indicatorTypeMap) {
        const indicatorMap = {};

        for (const [ key, indicator ] of Object.entries(indicators)) {
            const ind = { ...indicator };
            const { actual, planned } = this.getIndicatorValue(indicator, indicatorTypeMap, 'both');
            if (indicator.actualTotal) ind.actualTotal = actual;
            if (indicator.plannedTotal) ind.plannedTotal = planned;
            indicatorMap[key] = ind;
        }

        return indicatorMap;
    }


    /**
     * Converts the planning indicator value from the fraction-less integer from the server into a readable format
     * The return type is dependent on whether the indicator contains planned/actual values or both (combined)
     * @param indicator {Object} The indicator to convert
     * @param indicatorTypeMap {Object} The indicator type map from which the size of the fractional part can be retrieved
     * @param forceType {'planned'|'actual'|'combined'|'both'|null} Force the return type to be of the given value
     * @returns {null|number|{actual: number, planned: number}}
     */
    getIndicatorValue(indicator, indicatorTypeMap, forceType = null) {
        /** @type {number|null}*/
        let plannedFractional = indicator.plannedTotal === 0 ? 0 : null;
        /** @type {number|null}*/
        let actualFractional = indicator.actualTotal === 0 ? 0 : null;

        if (indicator.plannedTotal > 0)
            plannedFractional = indicator.plannedTotal / Math.pow(10, indicatorTypeMap[indicator.typeId].fractional);
        if (indicator.actualTotal > 0)
            actualFractional = indicator.actualTotal / Math.pow(10, indicatorTypeMap[indicator.typeId].fractional);

        if (!forceType)
            if (plannedFractional && actualFractional)
                forceType = 'combined';
            else if (plannedFractional)
                forceType = 'planned';
            else if (actualFractional)
                forceType = 'actual';

        switch (forceType) {
            case 'planned':
                return plannedFractional;
            case 'actual':
                return actualFractional;
            case 'combined':
                return actualFractional ? actualFractional : plannedFractional;
            case 'both':
                return { actual: actualFractional, planned: plannedFractional };
            default:
                return null;
        }
    }

    /**
     * Filters shifts based on the given parameters
     * @param shifts {Object|Array}
     * @param from {Moment}
     * @param to {Moment}
     * @param mode {'day'|'week'|'month'|'monthHourly'}
     * @param employeeIds {Array|Set} The employee ids to filter the shifts; the ids could be filtered by their spotId etc.
     * @returns {Object}
     */
    filterShifts(shifts, from, to, mode, employeeIds = []) {
        if (!( employeeIds instanceof Set ))
            employeeIds = new Set(employeeIds);

        if (!Array.isArray(shifts))
            shifts = Object.values(shifts);

        const filteredShifts = {};

        for (const shift of shifts) {
            // Check if employee is in the given employeeIds
            if (shift.employeeId && employeeIds.size > 0 && !employeeIds.has(shift.employeeId)) continue;

            // Check if shift is in the given time range
            const shiftFrom = this.moment(shift.from);
            const shiftTo = this.moment(shift.to);

            if (shiftFrom.isSameOrAfter(to) || shiftTo.isSameOrBefore(from)) continue;

            // if (UNIT_MAP[mode] === 'days') {
            // If the unit is days, use only the start of the shift as the key and ignore the length
            // const key = shiftFrom.format(KEY_FORMATTERS[mode]);
            // if (!filteredShifts[key])
            //     filteredShifts[key] = [];
            // filteredShifts[key].push(shift);
            // } else {
            // If the unit is not days, add the shift to all hours in the shift's range
            for (let start = shiftFrom; start.isSameOrBefore(shiftTo, UNIT_MAP[mode]); start.add(1, UNIT_MAP[mode])) {
                const key = start.format(KEY_FORMATTERS[mode]);
                if (!filteredShifts[key])
                    filteredShifts[key] = [];
                filteredShifts[key].push(shift);
            }
            // }
        }

        return filteredShifts;
    }

    /**
     * Packs the shifts into groups of given interval
     * @param shifts {Object|Array}
     * @param from {Moment}
     * @param to {Moment}
     * @param mode {'day'|'week'|'month'|'monthHourly'}
     * @param interval {number} The size of the interval in the given unit
     * @returns {Object}
     */
    combineShifts(shifts, from, to, mode, interval = 4) {
        const unit = UNIT_MAP[mode];
        if (!Array.isArray(shifts))
            shifts = Object.values(shifts);
        if (shifts.length === 0)
            return {};
        // If items are arrays, flatten them
        if (Array.isArray(shifts[0]))
            shifts = shifts.flat();

        const combinedShifts = {};

        const shiftMomentMap = {};

        for (const shift of shifts) {
            const shiftFrom = this.moment(shift.from);
            const shiftTo = this.moment(shift.to);
            shiftMomentMap[shift.id] = { from: shiftFrom, to: shiftTo };
        }

        for (const date = this.moment(from); date.isBefore(to); date.add(interval, unit)) {
            const key = date.format(KEY_FORMATTERS[mode]);
            combinedShifts[key] = {}
            const endDate = this.moment(date).add(interval, unit);

            for (const shift of shifts) {
                const shiftFrom = shiftMomentMap[shift.id].from;
                const shiftTo = shiftMomentMap[shift.id].to;
                if (date.isBetween(shiftFrom, shiftTo) || endDate.isBetween(shiftFrom, shiftTo)) {
                    combinedShifts[key][shift.id] = shift;
                }
            }
        }

        // Remove ids from the combined shifts and return them
        return Object.fromEntries(Object.entries(combinedShifts).map(([ key, shiftObj ]) => [ key, Object.values(shiftObj) ]));
    }

    /**
     * Returns a new object with the total amounts of shifts
     * @param shiftObj {Object}
     * @returns {{[key: string]: number}}
     */
    getShiftCounts(shiftObj) {
        const shiftCounts = {};
        for (const [ key, shifts ] of Object.entries(shiftObj)) {
            shiftCounts[key] = shifts.length;
        }
        return shiftCounts;
    }

    combineActualShiftBreaks(actualShifts, actualBreaks) {
        if (Array.isArray(actualShifts))
            actualShifts = Object.fromEntries(actualShifts.map(e => [ e.id, e ]));
        if (!Array.isArray(actualBreaks))
            actualBreaks = Object.values(actualBreaks);

        for (const actualBreak of actualBreaks) {
            let actualShift = actualShifts[actualBreak.shiftId];
            if (!actualShift) continue;
            actualShift = { breaks: {}, breakValue: 0, ...actualShift };

            actualShift.breaks[actualBreak.id] = actualBreak;
            // sum the breakValue in minutes
            actualShift.breakValue += this.moment(actualBreak.to).diff(this.moment(actualBreak.from), 'minutes', true);

            actualShifts[actualShift.id] = actualShift;
        }

        return actualShifts;
    }


    /**
     * Converts shiftTemplates to an array of (pseudo-)planShifts
     * @param shiftTemplates {Array|Object}
     * @param planShifts {Array|Object}
     * @param from {Moment}
     * @param to {Moment}
     * @return {Object[]}
     */
    convertShiftTemplateToShifts(shiftTemplates, planShifts, from, to) {
        if (!Array.isArray(shiftTemplates))
            shiftTemplates = Object.values(shiftTemplates);

        if (shiftTemplates.length === 0) return [];

        if (!Array.isArray(planShifts))
            planShifts = Object.values(planShifts);

        const planShiftsByTemplateId = {};
        // const unassociatedPlanShifts = [];
        for (const planShift of planShifts) {
            if (!planShift.templateId) {
                // unassociatedPlanShifts.push(planShift);
                continue;
            }
            if (!planShiftsByTemplateId[planShift.templateId])
                planShiftsByTemplateId[planShift.templateId] = [];
            planShift.momentFrom = this.moment(planShift.from);
            planShiftsByTemplateId[planShift.templateId].push(planShift);
        }

        // Filter templates that are not in the given time range and store it [count] times in an object by weekday
        const templatesForWeekday = {};
        for (const template of shiftTemplates) {
            const validFrom = template.validFrom ? this.moment(template.validFrom) : null;
            const validTo = template.validTo ? this.moment(template.validTo) : null;

            if (validFrom && validFrom.isAfter(to)) continue;
            if (validTo && validTo.isBefore(from)) continue;

            for (let wd of template.weekDays) {
                if (wd === 7) wd = 0;
                wd = wd.toString();

                if (!templatesForWeekday[wd])
                    templatesForWeekday[wd] = [];

                // for (let i = 0; i < template.count; i++)
                templatesForWeekday[wd].push(template);
            }
        }

        // const shifts = [ ...unassociatedPlanShifts ];
        const shifts = [];
        for (const date = this.moment(from); date.isBefore(to); date.add(1, 'days')) {
            const weekday = date.day();
            const templates = templatesForWeekday[weekday];
            if (!templates) continue;

            for (const template of templates) {
                const [ fromHour, fromMinute ] = template.from.split(':');
                const shiftFrom = this.moment(date).hour(parseInt(fromHour)).minute(parseInt(fromMinute));
                const [ toHour, toMinute ] = template.to.split(':');
                const shiftTo = this.moment(date).hour(parseInt(toHour)).minute(parseInt(toMinute));
                if (shiftTo.isBefore(shiftFrom)) shiftTo.add(1, 'days');

                const planShifts = ( planShiftsByTemplateId[template.id] || [] )
                    .filter(({ momentFrom: planShiftFrom }) => planShiftFrom.isSame(shiftFrom, 'days'))
                    .map((planShift) => ( {
                        ...planShift,
                        from: shiftFrom.format(),
                        fromDate: shiftFrom,
                        momentFrom: shiftFrom,
                        to: shiftTo.format(),
                        toDate: shiftTo,
                        momentTo: shiftTo,
                    } ));

                let planShiftLength = 0;
                if (planShifts) {
                    shifts.push(...planShifts);
                    planShiftLength = planShifts.length;
                }

                for (let i = 0; i < template.count - planShiftLength; i++)
                    shifts.push(this.createShiftFromTemplate(template, date));

            }
        }

        return shifts;
    }

    /**
     * Filters the employees by spotIds
     * @param employees {Object|Array}
     * @param employeeLevels {number[]}
     * @param spotMap {Object}
     * @returns {Set<string>} The filtered employee ids
     */
    filterEmployeeIds(employees, employeeLevels, spotMap) {
        if (!Array.isArray(employees))
            employees = Object.values(employees);
        if (employees.length === 0)
            return new Set();

        const filteredIds = new Set();
        for (const employee of employees) {
            if (!employee.spotId) {
                if (employeeLevels.includes(1)) filteredIds.add(employee.id);
                continue;
            }
            const spot = spotMap[employee.spotId];
            if (spot && employeeLevels.includes(spot.employeeLevel))
                filteredIds.add(employee.id);
        }

        return filteredIds;
    }


    /******************************************************************************/
// Calculation functions                                                      */
    /******************************************************************************/

    /**
     * Calculates the total hours of workTime in the given shifts
     * @param shifts {Object|Array}
     * @param from {Moment|null}
     * @param to {Moment|null}
     * @param employeeIds {Array|Set} The employee ids to filter the shifts; the ids could be filtered by their spotId etc.
     * @param subtractBreakTime {boolean} Whether to subtract the break time from the total hours
     * @returns {[number, number]} Returns the workTime (with breaks subtracted, if specified) and the breakTime
     */
    calculateWorkTime(shifts, from = null, to = null, employeeIds = [], subtractBreakTime = false) {
        if (!( employeeIds instanceof Set ))
            employeeIds = new Set(employeeIds);

        if (!Array.isArray(shifts))
            shifts = Object.values(shifts);
        if (shifts.length === 0)
            return [ 0, 0, 0, 0 ];
        // If items are arrays, flatten them
        if (Array.isArray(shifts[0]))
            shifts = shifts.flat();

        let shiftCount = 0;
        let workTime = 0;
        let entityCount = 0;
        let breakTimes = {};

        for (const shift of shifts) {
            const shiftFrom = this.moment(shift.from);
            const shiftTo = this.moment(shift.to);

            if (shift.employeeId && employeeIds.size > 0 && !employeeIds.has(shift.employeeId)) continue;

            // Check if shift is in the given time range
            if (to && shiftFrom.isAfter(to) || from && shiftTo.isBefore(from))
                continue;

            // Cut the shift to the given time range if overflow
            let relevantFrom = shiftFrom;
            let relevantTo = shiftTo;
            if (from && relevantFrom.isBefore(from))
                relevantFrom = this.moment(from);
            if (to && relevantTo.isAfter(to))
                relevantTo = this.moment(to);

            // Calculate relevant workTime (workTime in the given range)
            const shiftTime = this.moment(relevantTo).diff(relevantFrom, 'hours', true);

            shiftCount += 1;
            workTime += shiftTime;
            //add the aliquote shift count
            entityCount += relevantTo.diff(relevantFrom) / ( shiftTo.diff(shiftFrom) );
            // Calculate full workValue to get the fractional break value
            const workValue = shiftTo.diff(shiftFrom, 'hours', true);
            // Calculate break
            if (shift.breakValue && !breakTimes[shift.id]) {
                const partialBreakValue = ( shift.breakValue / 60 ) / workValue;
                breakTimes[shift.id] = shiftTime * partialBreakValue;
            }
        }

        const breakTime = Object.values(breakTimes).reduce((a, b) => a + b, 0);

        if (subtractBreakTime)
            workTime -= breakTime;

        return [ workTime, breakTime, entityCount, shiftCount ];
    }

    /**
     * Calculates actual, assigned, planned and free workTimes and groups them by the given unit
     * @param planShifts {Object} The object from filterShifts()
     * @param actualShifts {Object}
     * @param mode {'day'|'week'|'month'|'monthHourly'}
     * @param from {Moment}
     * @param to {Moment}
     * @param employeeIds {Array|Set} The employee ids to filter the shifts; the ids could be filtered by their spotId etc.
     * @param subtractBreaks
     * @returns {{[string]: {actual: number, actualBreak: number, assigned: number, assignedBreak: number, planned: number, plannedBreak: number, free: number, freeBreak: number}}}
     */
    calculateAllWorkTimes(planShifts, actualShifts, mode, from, to, employeeIds = [], subtractBreaks = false) {
        if (!( employeeIds instanceof Set ))
            employeeIds = new Set(employeeIds);

        if (!planShifts) planShifts = {};
        if (!actualShifts) actualShifts = {};

        const workTimes = {};

        const unit = UNIT_MAP[mode];
        const keyFormatter = KEY_FORMATTERS[mode];

        from = momentOrig(from);
        to = momentOrig(to);

        for (const date = momentOrig(from); date.isBefore(to); date.add(1, unit)) {
            // If the unit is days, do not cut the shifts to the given time range
            const startDate = unit === 'days' ? null : momentOrig(date);
            const endDate = unit === 'days' ? null : momentOrig(startDate).add(1, unit);

            const key = momentOrig(date).format(keyFormatter);

            const planShiftsForUnit = planShifts[key] || [];
            let actualShiftsForUnit = actualShifts[key] || [];

            const [ actualWorkTime, actualBreakTime ] = this.calculateWorkTime(actualShiftsForUnit, startDate, endDate, employeeIds, false);
            const entry = {
                actual: subtractBreaks ? actualWorkTime - actualBreakTime : actualWorkTime,
                actualWorkTime: actualWorkTime,
                actualBreak: actualBreakTime,
                assigned: 0,
                assignedWorkTime: 0,
                assignedBreak: 0,
                planned: 0,
                plannedWorkTime: 0,
                plannedBreak: 0,
                free: 0,
                freeWorkTime: 0,
                freeBreak: 0,
            }

            const freeShifts = [];
            const assignedShifts = [];

            for (const planShift of planShiftsForUnit) {
                if (planShift.employeeId)
                    assignedShifts.push(planShift);
                else
                    freeShifts.push(planShift);
            }

            const [ assignedWorkTime, assignedBreakTime ] = this.calculateWorkTime(assignedShifts, startDate, endDate, employeeIds, false);
            entry.assigned += subtractBreaks ? assignedWorkTime - assignedBreakTime : assignedWorkTime;
            entry.assignedWorkTime = assignedWorkTime;
            entry.assignedBreak += assignedBreakTime;
            entry.planned += subtractBreaks ? assignedWorkTime - assignedBreakTime : assignedWorkTime;
            entry.plannedWorkTime += assignedWorkTime;
            entry.plannedBreak += assignedBreakTime;

            const [ freeWorkTime, freeBreakTime ] = this.calculateWorkTime(freeShifts, startDate, endDate, [], false);
            entry.free += subtractBreaks ? freeWorkTime - freeBreakTime : freeWorkTime;
            entry.freeWorkTime += freeWorkTime;
            entry.freeBreak += freeBreakTime;
            entry.planned += subtractBreaks ? freeWorkTime - freeBreakTime : freeWorkTime;
            entry.plannedWorkTime += freeWorkTime;
            entry.plannedBreak += freeBreakTime;

            workTimes[key] = entry;
        }

        return workTimes;
    }


    calculateRegularWorkTime(workTimeRegular, employeeLevels = [ 1, 2 ]) {
        let workTime = 0;

        for (const { employeeLevel, workTimeRegular: wt } of workTimeRegular) {
            if (!employeeLevels.includes(employeeLevel)) continue;

            workTime += wt;
        }

        return workTime / 60 / 60;
    }

    calculateUnproductiveWorkTime(sumStats, employeeLevels = [ 1, 2 ]) {
        let workTimeUnproductive = 0;

        for (const { unproductiveWorkTime, unproductiveWorkTimeCrew } of sumStats) {
            if (employeeLevels.includes(1) && employeeLevels.includes(2))
                workTimeUnproductive += unproductiveWorkTime;
            else if (employeeLevels.includes(1))
                workTimeUnproductive += unproductiveWorkTimeCrew;
            else if (employeeLevels.includes(2))
                workTimeUnproductive += unproductiveWorkTime - unproductiveWorkTimeCrew;
        }

        return workTimeUnproductive / 60 / 60;
    }

    /*
    async calculateAvailableWorkTime(employees, employeeContractMap, allowedContractMap, employeePreferenceMap,
                                     monthStart, employeeIds = [], holidays = []) {
        if (!( employeeIds instanceof Set ))
            employeeIds = new Set(employeeIds);

        if (Array.isArray(employees))
            employees = Object.fromEntries(employees.map(e => [ e.id, e ]));

        const startDate = this.moment(monthStart).startOf('month');
        const endDate = this.moment(monthStart).endOf('month').add(1, 'day').startOf('day');

        let workTime = 0;

        for (const employee of Object.values(employees)) {
            if (employeeIds.size > 0 && !employeeIds.has(employee.id)) continue;

            const contracts = Object.values(employeeContractMap).filter(c => c.employeeId === employee.id);

            if (contracts.length === 0) continue;

            const wtData = this.rosterHelper.GetWorktimeData(
                monthStart,
                startDate,
                endDate,
                contracts,
                allowedContractMap,
                this.storeSettings,
                this.storeSettings.workTimeMode,
                null,
                this.storeSettings.exitFactoring,
                this.helpers,
                true,
                holidays
            );

            workTime += wtData.workTime;
        }

        let empIds = Object.keys(employees)
        if (employeeIds.size > 0)
            empIds = empIds.filter(id => employeeIds.has(id));

        let employeeWorkBalanceMap;
        if (this.helpers.store.state.employeeWorkbalanceMapState.type === 'employees-prevm' && this.helpers.store.state.employeeWorkbalanceMap) {
            employeeWorkBalanceMap = this.helpers.store.state.employeeWorkbalanceMap;
        } else {
            employeeWorkBalanceMap = await this.helpers.GetPreviousMonthEmployeeWorkbalanceMapForEmployees({ ids: empIds }, true, false, monthStart.month(), monthStart.year(), false);
        }

        // Get previous balance
        const prevMonthBalanceSum = Object.values(employeeWorkBalanceMap)
            .filter(ewb => !!employees[ewb.employeeId])
            .reduce((acc, ewb) => acc + ewb.workTimeBalance, 0);

        const employeePreferenceSum = this.calculatePreferenceWorkTimeSum(employees, employeeContractMap, allowedContractMap, employeePreferenceMap, startDate, endDate, employeeIds)

        const workTimeHours = Math.max(0, workTime / 60);
        const prevMonthBalance = prevMonthBalanceSum / 60;
        const preferenceHours = employeePreferenceSum / 60;

        return [ workTimeHours, prevMonthBalance, preferenceHours ];
    } */

    /**
     * Calculates user-definable workTime stats from original values
     * @param workTimeHours {number}
     * @param prevMonthBalance {number}
     * @param preferenceHours {number}
     * @param includePrevMonthTime {boolean}
     * @param useExistingPreferences {boolean}
     * @param absencePercent {number}
     * @returns {[number, number, number]} an array containing realAvailableWorkTimeHours, prevMonthBalance and employeePreferenceHours
     */
    calculateVariableWorkTimeStats(workTimeHours, prevMonthBalance, preferenceHours, includePrevMonthTime, useExistingPreferences, absencePercent = 1) {
        const employeePreferenceHours = useExistingPreferences ? preferenceHours : workTimeHours * absencePercent;

        let realAvailableWorkTimeHours = workTimeHours;
        realAvailableWorkTimeHours -= employeePreferenceHours;
        if (includePrevMonthTime)
            realAvailableWorkTimeHours -= prevMonthBalance;

        return [ realAvailableWorkTimeHours, prevMonthBalance, employeePreferenceHours ];
    }

    calculatePreferenceWorkTimeSum(employeeMap, employeeContractMap, allowedContractMap, employeePreferenceMap, startDate, endDate, employeeIds = []) {
        if (!( employeeIds instanceof Set ))
            employeeIds = new Set(employeeIds);
        const employeeContractMomentMap = this.rosterHelper.getFromToMomentMap(employeeContractMap);
        const employeeIdContractMap = this.rosterHelper.getEmployeeIdContractMap(employeeContractMap);
        const preferenceMomentMap = this.rosterHelper.getFromToMomentMap(employeePreferenceMap);

        let workValueSum = 0;

        const employeePreferences = Object.values(employeePreferenceMap).filter(c => !!employeeMap[c.employeeId]);
        for (const preference of employeePreferences) {
            if (!preference.approved || preference.state !== 1) continue;
            const employee = employeeMap[preference.employeeId];

            if (employeeIds.size > 0 && !employeeIds.has(employee.id)) continue;

            workValueSum += this.rosterHelper.GetEmployeePreferenceWorkTime(
                preference,
                preferenceMomentMap,
                employee,
                employeeIdContractMap,
                employeeContractMomentMap,
                allowedContractMap,
                startDate,
                endDate,
                this.storeSettings,
            );
        }

        return workValueSum;
    }


    /**
     * Calculates the sum of the indicators used to calculate recommended shifts
     * @param indicators {Object}
     * @param from {Moment|null}
     * @param to {Moment|null}
     * @param filter {'planned'|'actual'|'combined'} Which value to use for the sum; if 'combined', actual is preferred over planned
     * @param mode {'day'|'dayQuarterHourly'|'week'|'month'|'monthHourly'|null} Used to calculate real percentage
     * @returns {[number, number]} The sum of the indicators and the proportion of indicators with values in the given range
     */
    calculateIndicatorSum(indicators, from = null, to = null, filter = 'combined', mode = null) {
        let sum = 0;
        let indicatorsWithValues = 0;
        let validIndicators = 0;

        // let indicatorTimeSum = 0;

        for (const value of Object.values(indicators)) {
            let { actualTotal: actual, plannedTotal: planned } = value;
            if (!actual) actual = 0;
            if (!planned) planned = 0;

            if (from && this.moment(value.to).isBefore(from) || to && this.moment(value.from).isAfter(to)) continue;

            let summand = 0;

            switch (filter) {
                case 'planned':
                    summand = planned;
                    break;
                case 'actual':
                    summand = actual;
                    break;
                case 'combined':
                default:
                    // Prefers actual if available
                    if (actual !== 0)
                        summand = actual;
                    else
                        summand = planned;
                    break;
            }

            if (summand !== 0) {
                sum += summand;
                indicatorsWithValues++;
            }
            // indicatorTimeSum += this.moment(value.to).diff(value.from, UNIT_MAP[mode], true);
            validIndicators++;
        }

        if (validIndicators === 0)
            return [ 0, 0, 0 ];

        const percent = mode
            ? indicatorsWithValues / ( this.moment(to).diff(from, UNIT_MAP[mode], true) / INTERVAL_MAP[mode] )
            : indicatorsWithValues / validIndicators;
        return [ sum, percent, sum / percent ];
    }

    /**
     * Calculates a map of the percentage of the indicators based on the sum
     * @param indicatorMap {Object}
     * @param indicatorSum {number}
     * @param filter {'planned'|'actual'|'combined'} Which value to use to calculate percent; if 'combined', actual is preferred over planned
     * @returns {Object[]}
     */
    calculateIndicatorPercent(indicatorMap, indicatorSum, filter = 'combined') {
        const indicatorValueMap = {};

        for (const [ key, indicator ] of Object.entries(indicatorMap)) {
            switch (filter) {
                case 'planned':
                    indicatorValueMap[key] = indicator.plannedTotal;
                    break;
                case 'actual':
                    indicatorValueMap[key] = indicator.actualTotal;
                    break;
                case 'combined':
                default:
                    indicatorValueMap[key] = indicator.actualTotal || indicator.plannedTotal;
            }
        }

        const indicatorPercent = Object.entries(indicatorValueMap).map(([ date, sales ]) => [
            date,
            sales / indicatorSum,
        ]);
        const indicatorPercentMap = Object.fromEntries(indicatorPercent);

        return [ indicatorPercentMap, indicatorValueMap ];
    }

    /**
     * Calculates the individual work times based on the percent of the indicators
     * @param indicatorPercentMap {{[key: string]: number}}
     * @param availableWorkTimeSum {number}
     * @returns {{[key: string]: number}}
     */
    calculateEmployeeWorkTimeMap(indicatorPercentMap, availableWorkTimeSum) {
        const employeeWorkTimeMap = Object.entries(indicatorPercentMap)
            .map(([ key, percent ]) => [ key, percent * availableWorkTimeSum ]);

        // noinspection JSValidateTypes
        return Object.fromEntries(employeeWorkTimeMap);
    }

    /**
     * Calculates the recommended shift count based on the individual work times
     * @param employeeWorkTimeMap {{[key: string]: number}}
     * @param divisor {number} The divisor to use for the calculation (60 if it's in minutes, use shiftLength if mode == 'day')
     * @returns {{[p: string]: string}}
     */
    calculateRecommendedEmployeeCount(employeeWorkTimeMap, divisor) {
        const hourlyEmployeeCounts = Object.entries(employeeWorkTimeMap).map(([ date, wt ]) => [ date, ( wt / divisor ) ]);

        return Object.fromEntries(hourlyEmployeeCounts);
    }

    /**
     * Combines market data by averaging values into blocks of the given size
     */
    combineMarketData(marketWorkTimeData, from, to, mode, interval) {
        const formatter = KEY_FORMATTERS[mode];
        const unit = UNIT_MAP[mode];

        const marketMap = {};
        for (const entry of marketWorkTimeData) {
            const key = this.moment(entry.date).format(formatter);
            marketMap[key] = entry;
        }

        const objKeys = [ 'ax', 'min', 'q1', 'q3', 'workTimeAvg', 'workTimeDev' ];

        const combinedData = [];
        for (const date = this.moment(from); date.isBefore(to); date.add(interval, unit)) {
            // const combinedKey = date.format(formatter);

            // Create combined entry and init with 0
            const newEntry = { date: this.moment(date) };
            for (const objKey of objKeys) newEntry[objKey] = 0;

            // Iterate over entries in the interval and add them to new entry
            for (let i = 0; i < interval; i++) {
                const k = this.moment(date).add(i, unit).format(formatter);
                const entry = marketMap[k];
                if (entry) {
                    for (const objKey of objKeys) {
                        newEntry[objKey] += entry[objKey] || 0;
                    }
                }
            }

            // (Don't) Take average
            // for (const objKey of objKeys) newEntry[objKey] /= interval;

            // Push entry
            combinedData.push(newEntry);
        }

        return combinedData;
    }

    /**
     * Calculates the recommended shift count for the market
     * @param marketPlannedWorkTimeData {{workTimeAvg: number, workTimeDev: number, q1: number, q3: number, date: string}[]}
     * @param chartSettings {Object}
     * @param mode {'day'|'week'|'month'|'monthHourly'}
     * @param divisor {number} The divisor to use for the calculation (use shiftLength if mode == 'day')
     * @return {{[key: string]: {upper: number, lower: number}}}
     */
    calculateMarketEmployeeCount(marketPlannedWorkTimeData, chartSettings, mode, divisor = 1) {
        if (!marketPlannedWorkTimeData || marketPlannedWorkTimeData.length === 0) return {};
        const map = {};

        for (const entry of marketPlannedWorkTimeData) {
            const key = this.moment(entry.date).format(KEY_FORMATTERS[mode]);
            let upper = 0;
            let lower = 0;
            if (chartSettings.selectedMarketMode == null || chartSettings.selectedMarketMode === 1 || chartSettings.selectedMarketMode === 3) {
                upper = ( entry.workTimeAvg + entry.workTimeDev ) / 3600.0 / divisor;
                lower = Math.max(0, ( entry.workTimeAvg - entry.workTimeDev ) / 3600.0 / divisor);
            } else {
                upper = entry.q3 / 3600.0 / divisor;
                lower = Math.max(0, entry.q1 / 3600.0 / divisor);
            }

            map[key] = [ lower, upper ];
        }

        return map;
    }


    /**
     * Calculates the productive workTimeMap (total workTime from 00:00 to 24:00)
     * Used to calculate productivity
     * @param planShiftMap {Object}
     * @param actualShiftBreakMap {Object}
     * @param mode {'day'|'week'|'month'|'monthHourly'}
     * @param from {Moment}
     * @param to {Moment}
     * @param employeeIds {Array|Set}
     * @param spotMap {Object}
     * @returns {{[key: string]: {actual: number, previousActual: number, assigned: number, previousAssigned: number}}}
     */
    calculateProductiveWorkTime(planShiftMap, actualShiftBreakMap, mode, from, to, employeeIds = [], spotMap = {}) {
        if (!( employeeIds instanceof Set ))
            employeeIds = new Set(employeeIds);

        const getRelevantTime = (shift, from, to) => {
            let fromDate = this.moment(shift.from);
            let toDate = this.moment(shift.to);

            const realDiff = toDate.diff(fromDate, 'minutes', true);

            if (fromDate.isBefore(from)) fromDate = this.moment(from);
            if (toDate.isAfter(to)) toDate = this.moment(to);

            const relevantDiff = toDate.diff(fromDate, 'minutes', true);

            let relevantBreak = 0;
            if (shift.breaks && Object.values(shift.breaks).length > 0) {
                for (const b of Object.values(shift.breaks)) {
                    let breakFrom = this.moment(b.from);
                    let breakTo = this.moment(b.to);

                    if (breakFrom.isAfter(toDate)) continue;
                    if (breakTo.isBefore(fromDate)) continue;
                    if (breakFrom.isBefore(fromDate)) breakFrom = this.moment(fromDate);
                    if (breakTo.isAfter(toDate)) breakTo = this.moment(toDate);

                    const breakDiff = breakTo.diff(breakFrom, 'minutes', true);

                    relevantBreak += breakDiff > 0 ? breakDiff : 0;
                }
            } else if (shift.breakValue) {
                relevantBreak += shift.breakValue * relevantDiff / realDiff;
            }

            return ( relevantDiff - relevantBreak ) / 60;
        };

        const sumShiftTime = (shifts, from, to, planShifts = []) => {
            let sum = 0;
            for (const shift of shifts) {
                if (!shift.employeeId) continue;
                const planShift = shift.planshiftId ? planShifts.find(s => shift.planshiftId === s.id) : null;
                const spotId = shift.spotId || ( planShift || {} ).spotId;
                if (spotId && spotMap[spotId] && !spotMap[spotId].productive) continue;
                if (employeeIds.size > 0 && !employeeIds.has(shift.employeeId)) continue;
                if (this.moment(shift.from).isAfter(to)) continue;
                if (this.moment(shift.to).isBefore(from)) continue;
                sum += getRelevantTime(shift, from, to);
            }
            return sum;
        };

        const productiveWorkTimeMap = {};

        const unit = UNIT_MAP[mode];
        const interval = INTERVAL_MAP[mode];
        for (const date = this.moment(from); date.isBefore(to); date.add(interval, unit)) {
            const toDate = this.moment(date).add(interval, unit);
            const previousDate = this.moment(date).subtract(interval, unit);
            const previousKey = previousDate.format(KEY_FORMATTERS[mode]);
            const key = date.format(KEY_FORMATTERS[mode]);

            const actualShifts = actualShiftBreakMap[key] || [];
            const prevActualShifts = actualShiftBreakMap[previousKey] || [];
            const planShifts = planShiftMap[key] || [];
            const prevPlanShifts = planShiftMap[previousKey] || [];

            productiveWorkTimeMap[key] = {
                actual: sumShiftTime(actualShifts, date, toDate, planShifts),
                previousActual: sumShiftTime(prevActualShifts, date, toDate, prevPlanShifts),
                assigned: sumShiftTime(planShifts, date, toDate),
                previousAssigned: sumShiftTime(prevPlanShifts, date, toDate),
                // if planned workTimes should also be calculated, add a parameter to the function to filter employeeIds or not
            };
        }

        return productiveWorkTimeMap;
    }


    /**
     * Calculates productivity by [unit]
     * @param productiveWorkTimeMap {Object} The map of employee work times; result from calculateProductiveWorkTime
     * @param planningIndicatorMap {Object} The map of planning indicators; result from filterPlanningIndicators, etc.
     * @param mode {'day'|'week'|'month'|'monthHourly'}
     * @returns {{[key: string]: {actual: number, planned: number, combined: number}}}
     */
    calculateProductivity(productiveWorkTimeMap, planningIndicatorMap, mode = 'month') {
        const productivityMap = {};

        for (const [ key, productiveWT ] of Object.entries(productiveWorkTimeMap)) {
            const indicator = planningIndicatorMap[key];
            if (!indicator) continue;

            productivityMap[key] = {
                actual: null,
                planned: null,
                combined: null,
            };

            if (indicator.actualTotal && productiveWT.actual)
                productivityMap[key].actual = indicator.actualTotal / ( productiveWT.actual + ( mode === 'month' ? productiveWT.previousActual : 0 ) );
            if (indicator.plannedTotal && productiveWT.assigned)
                productivityMap[key].planned = indicator.plannedTotal / ( productiveWT.assigned + ( mode === 'month' ? productiveWT.previousAssigned : 0 ) );
            productivityMap[key].combined = productivityMap[key].actual || productivityMap[key].planned;
        }

        return productivityMap;
    }


    /**
     * Maps employee feedback
     * @param sumStats {Array}
     * @param mode
     * @returns {{[key: string]: number}}
     */
    mapEmployeeFeedback(sumStats, mode) {
        const feedbackMap = {};

        for (const entry of sumStats) {
            const key = this.moment(entry.date).format(KEY_FORMATTERS[mode]);
            // noinspection JSUnresolvedVariable
            feedbackMap[key] = entry.actualShiftFeedback;
        }

        return feedbackMap;
    }


    calculateMEOData(meoData, from, to, mode, divisor = 1, spotIds = []) {
        const unit = UNIT_MAP[mode];

        const meoDataMap = {};
        for (const entry of meoData) {
            const key = this.moment(entry.date).format(KEY_FORMATTERS[mode]);
            if (!meoDataMap[key]) meoDataMap[key] = {};
            if (!meoDataMap[key][entry.spotId])
                meoDataMap[key][entry.spotId] = {
                    date: entry.date,
                    spotId: entry.spotId,
                    actual: null,
                    planned: null,
                };
            if (entry.planned) meoDataMap[key][entry.spotId].planned = entry.workTime;
            else meoDataMap[key][entry.spotId].actual = entry.workTime;
        }

        const meoMap = {};
        for (const date = this.moment(from); date.isBefore(to); date.add(1, unit)) {
            const toDate = this.moment(date).add(1, unit);
            const key = date.format(KEY_FORMATTERS[mode]);

            if (!meoMap[key]) meoMap[key] = { actual: null, planned: null, combined: null };

            const meoBySpot = meoDataMap[key];
            if (!meoBySpot) continue;

            const wantedSpots = spotIds.length > 0 ? spotIds : Object.keys(meoBySpot);
            for (const spotId of wantedSpots) {
                const meo = meoBySpot[spotId];
                if (!meo) continue;

                meoMap[key] = {
                    date: this.moment(date),
                    actual: ( meoMap[key].actual || 0 ) + ( meo.actual ? meo.actual / divisor : 0 ),
                    planned: ( meoMap[key].planned || 0 ) + ( meo.planned ? meo.planned / divisor : 0 ),
                };
                meoMap[key] = { ...meoMap[key], combined: meoMap[key].actual || meoMap[key].planned };
            }
        }

        return meoMap;
    }


    combineMEOData(meoMap, from, to, mode, interval = 4) {
        const unit = UNIT_MAP[mode];

        const combinedMEO = {};

        const keyFormatter = KEY_FORMATTERS[mode];
        for (const date = this.moment(from); date.isBefore(to); date.add(interval, unit)) {
            const key = date.format(keyFormatter);
            combinedMEO[key] = { date: this.moment(date), actual: 0, planned: 0, combined: 0 };

            for (let i = 0; i < interval; i++) {
                const meoKey = this.moment(date).add(i, unit).format(keyFormatter);
                const meo = meoMap[meoKey];
                if (!meo) continue;
                combinedMEO[key].actual += meo.actual;
                combinedMEO[key].planned += meo.planned;
                combinedMEO[key].combined += meo.combined;
            }
        }

        return combinedMEO;
    }


    /**
     * Calculates stats from rawData that can be displayed to the user
     * @param rawData
     * @param from
     * @param to
     * @param mode
     * @param chartSettings
     * @returns {{
     *    mode,
     *    from: Moment,
     *    to: Moment,
     *    actualIndicatorSum: number,
     *    plannedIndicatorSum: number,
     *    combinedIndicatorSum: number,
     *    coverage: number,
     *    prediction: number,
     *    utilization: number,
     *    feedbackAvg: number,
     *    feedbackUntil: Moment,
     *    feedbackCoverage: number,
     *    accuracy: {actual: number, planned: number, combined: number},
     *    understaffing: {actual: number, planned: number, combined: number},
     *    understaffingPercent: {actual: number, planned: number, combined: number},
     *    overstaffing: {actual: number, planned: number, combined: number},
     *    overstaffingPercent: {actual: number, planned: number, combined: number},
     *    workTimeSums: {actual: number, assigned: number, planned: number, free: number},
     *    productivity: {actual: number, actualMin: number, actualMax: number, planned: number, deviation: number},
     *    perUnit: {},
     *    originalWorkTimeStats: {},
     *    variableWorkTimeStats: {},
     * }}
     */
    calculateStats(rawData, from, to, mode, chartSettings) {
        if (!rawData) rawData = {};

        let combinedOverstaffingSum = 0;
        let combinedUnderstaffingSum = 0;
        let actualOverstaffingSum = 0;
        let actualUnderstaffingSum = 0;
        let plannedOverstaffingSum = 0;
        let plannedUnderstaffingSum = 0;
        let combinedRecommendedSum = 0;
        let actualRecommendedSum = 0;
        let plannedRecommendedSum = 0;
        let actualShiftSum = 0;
        let assignedShiftSum = 0;
        let plannedShiftSum = 0;
        let freeShiftSum = 0;
        let actualHourSum = 0;
        let relevantActualHourSum = 0;
        let assignedHourSum = 0;
        let relevantAssignedHourSum = 0;
        let plannedHourSum = 0;
        let freeHourSum = 0;
        let actualProductivitySum = 0;
        let actualProductivityCount = 0;
        let plannedProductivitySum = 0;
        let plannedProductivityCount = 0;
        let actualProductivityMax = null;
        let actualProductivityMin = null;
        let combinedProductivitySum = 0;
        let combinedProductivityCount = 0;
        let actualIndicatorSum = 0;
        let plannedIndicatorSum = 0;
        let combinedIndicatorSum = 0;
        let actualIndicatorCount = 0;
        let plannedIndicatorCount = 0;
        let actualIndicatorForPredSum = 0;
        let plannedIndicatorForPredSum = 0;
        let actualIndicatorForPredCount = 0;
        let plannedIndicatorForPredCount = 0;
        let feedbackSum = 0;
        let feedbackCount = 0;

        const currentDate = this.moment().startOf('day');
        const currentDateAccurate = this.moment();

        let actualShiftsUntil = this.moment(from);
        let plannedShiftsUntil = this.moment(from);
        let feedbackUntil = this.moment(from);
        let predictionUntil = this.moment(from);
        let actualIndicatorUntil = this.moment(from);
        let plannedIndicatorUntil = this.moment(from);

        const additionalData = rawData.additionalData || {};

        const statsPerUnit = {};

        for (const date = this.moment(from); date.isBefore(to); date.add(INTERVAL_MAP[mode], UNIT_MAP[mode])) {
            const key = date.format(KEY_FORMATTERS[mode]);
            const data = rawData[key] || {};

            if (data.actualShifts) {
                for (const shift of data.actualShifts) {
                    const shiftFrom = this.moment(shift.from);
                    if (actualShiftsUntil.isBefore(shiftFrom))
                        actualShiftsUntil = shiftFrom;
                }
            }
            if (data.planShifts && data.planShifts.length > 0) {
                plannedShiftsUntil = this.moment(date);
            }
            const indicator = data.indicator || { actualTotal: 0, plannedTotal: 0 };
            if (indicator.actualTotal) {
                predictionUntil = this.moment(date);
                actualIndicatorUntil = this.moment(date).add(INTERVAL_MAP[mode], UNIT_MAP[mode]);
            }
            if (indicator.plannedTotal) {
                predictionUntil = this.moment(date);
                plannedIndicatorUntil = this.moment(date).add(INTERVAL_MAP[mode], UNIT_MAP[mode]);
            }
        }

        if (actualShiftsUntil.isAfter(this.moment()))
            actualShiftsUntil = this.moment();
        if (predictionUntil.isAfter(currentDate))
            predictionUntil = currentDate;
        if (actualIndicatorUntil.isAfter(currentDate))
            actualIndicatorUntil = currentDate;

        const displayData = new Map();

        const wantedDisplayData = Object.entries(chartSettings.displayData || {});
        if (wantedDisplayData.length > 0) {
            for (const [ displayKey, displayValue ] of wantedDisplayData) {
                if (displayValue) {
                    displayData.set(displayKey, displayValue);
                }
            }
        } else {
            displayData.set('lines', [ 'actual', 'planned', 'market' ]);
            displayData.set('bars', [ 'actual', 'assigned', 'overstaffed', 'free' ]);
            displayData.set('selectedStat', null);
        }

        for (const date = this.moment(from); date.isBefore(to); date.add(INTERVAL_MAP[mode], UNIT_MAP[mode])) {
            const key = date.format(KEY_FORMATTERS[mode]);
            const data = rawData[key] || {};
            const dateTo = this.moment(date).add(INTERVAL_MAP[mode], UNIT_MAP[mode]);

            const assignedShifts = this.calculateRelevantShiftValue(mode, data.assignedShifts, chartSettings, date, dateTo); //, 'assigned');
            const plannedShifts = this.calculateRelevantShiftValue(mode, data.planShifts, chartSettings, date, dateTo); //, 'planned');
            const actualShifts = this.calculateRelevantShiftValue(mode, data.actualShifts, chartSettings, date, dateTo); //, 'actual');
            const freeShifts = this.calculateRelevantShiftValue(mode, data.freeShifts, chartSettings, date, dateTo); //, 'free');
            const workTime = data.workTime || { actual: 0, assigned: 0, planned: 0, free: 0 };
            const indicator = data.indicator || { actualTotal: 0, plannedTotal: 0 };
            const productivity = data.productivity || { actual: 0, planned: 0, combined: 0 };
            const recommendedEmployeeCounts = chartSettings.calculationMode === 'vlh'
                ? data.vlhValue || {}
                : chartSettings.calculationMode === 'meo'
                    ? data.meoValue || {}
                    : data.recommendedEmployeeCount || {};
            const combinedRecommendedEmployees = recommendedEmployeeCounts.combined || ( mode === 'day' ? 0 : null );
            const actualRecommendedEmployees = recommendedEmployeeCounts.actual || ( mode === 'day' ? 0 : null );
            const plannedRecommendedEmployees = recommendedEmployeeCounts.planned || ( mode === 'day' ? 0 : null );
            const marketEmployeeCounts = data.marketEmployeeCount || [];
            const meo = data.meoValue || { actual: null, planned: null, combined: null };
            const vlh = data.vlhValue || { actual: null, planned: null, combined: null };

            // These 3 sums are used to display the correct data when switching between actual, planned and combined in the chart
            let combinedOverstaffing = 0;
            let combinedUnderstaffing = 0;
            let actualOverstaffing = 0;
            let actualUnderstaffing = 0;
            let plannedOverstaffing = 0;
            let plannedUnderstaffing = 0;

            let assignedHoursOrShifts;
            if (!date.isSame(currentDateAccurate, UNIT_MAP[mode])) {
                assignedHoursOrShifts = displayData.get('bars').includes('actual')
                    ? actualShifts || assignedShifts
                    : assignedShifts;
            } else {
                assignedHoursOrShifts = 0;
                let relevantPlanShifts = ( data.planShifts || [] ).filter(ps => ps.employeeId);
                if (displayData.get('bars').includes('actual')) {
                    assignedHoursOrShifts = this.calculateRelevantShiftValue(mode, data.actualShifts, chartSettings, date, dateTo);
                    const employeesWithActualShifts = [];
                    for (const as of ( data.actualShifts || [] )) {
                        employeesWithActualShifts.push(as.employeeId);
                    }
                    relevantPlanShifts = relevantPlanShifts.filter(ps => !employeesWithActualShifts.includes(ps.employeeId));
                }
                const relPlanShiftValue = this.calculateRelevantShiftValue(mode, relevantPlanShifts, chartSettings, date, dateTo);
                assignedHoursOrShifts += relPlanShiftValue;
            }

            if (actualRecommendedEmployees != null && date.isSameOrBefore(actualIndicatorUntil)) {
                if (assignedHoursOrShifts > actualRecommendedEmployees)
                    actualOverstaffing = assignedHoursOrShifts - actualRecommendedEmployees;
                else if (assignedHoursOrShifts < actualRecommendedEmployees)
                    actualUnderstaffing = actualRecommendedEmployees - assignedHoursOrShifts;

                actualOverstaffingSum += actualOverstaffing;
                actualUnderstaffingSum += actualUnderstaffing;
                actualRecommendedSum += actualRecommendedEmployees;
            }

            if (plannedRecommendedEmployees != null && date.isSameOrBefore(plannedIndicatorUntil)) {
                if (assignedHoursOrShifts > plannedRecommendedEmployees)
                    plannedOverstaffing = assignedHoursOrShifts - plannedRecommendedEmployees;
                else if (assignedHoursOrShifts < plannedRecommendedEmployees)
                    plannedUnderstaffing = plannedRecommendedEmployees - assignedHoursOrShifts;

                plannedOverstaffingSum += plannedOverstaffing;
                plannedUnderstaffingSum += plannedUnderstaffing;
                plannedRecommendedSum += plannedRecommendedEmployees;
            }

            // Tag bis heute: gibts ist werte, sonst planwerte, wenn kein werte, dann überstunden
            // am heutigen Tag: planwerte
            // dann stempelzeiten ansonsten planzeiten abziehen
            if (combinedRecommendedEmployees != null) {
                let actualOrAssignedShifts = assignedHoursOrShifts;
                let actualOrPlannedEmployees = combinedRecommendedEmployees; // date.isSameOrBefore(actualIndicatorUntil, UNIT_MAP[mode]) ? ( actualRecommendedEmployees || plannedRecommendedEmployees ) : plannedRecommendedEmployees;

                if (actualOrAssignedShifts > actualOrPlannedEmployees || !actualOrPlannedEmployees)
                    combinedOverstaffing = actualOrAssignedShifts - ( actualOrPlannedEmployees || 0 );
                else if (actualOrAssignedShifts < actualOrPlannedEmployees)
                    combinedUnderstaffing = actualOrPlannedEmployees - actualOrAssignedShifts;

                combinedOverstaffingSum += combinedOverstaffing;
                combinedUnderstaffingSum += combinedUnderstaffing;
                combinedRecommendedSum += actualOrPlannedEmployees;
            }

            actualProductivitySum += productivity.actual;
            actualProductivityCount += productivity.actual ? 1 : 0;
            plannedProductivitySum += productivity.planned;
            plannedProductivityCount += productivity.planned ? 1 : 0;
            combinedProductivitySum += productivity.combined;
            combinedProductivityCount += productivity.combined ? 1 : 0;
            if (actualProductivityMax === null || productivity.actual > actualProductivityMax)
                actualProductivityMax = productivity.actual;
            if (actualProductivityMin === null || ( productivity.actual && productivity.actual < actualProductivityMin ))
                actualProductivityMin = productivity.actual;

            const feedback = data.feedbackAvg;
            feedbackSum += feedback;
            feedbackCount += feedback ? 1 : 0;
            if (feedback) feedbackUntil = this.moment(date);

            if (!isNaN(indicator.actualTotal)) {
                actualIndicatorSum += indicator.actualTotal;
                if (indicator.actualTotal > 0)
                    actualIndicatorCount += 1;
            }
            if (!isNaN(indicator.plannedTotal)) {
                plannedIndicatorSum += indicator.plannedTotal;
                if (indicator.plannedTotal > 0)
                    plannedIndicatorCount += 1;
            }

            if (date.isSameOrBefore(predictionUntil)) {
                if (indicator.actualTotal) {
                    actualIndicatorForPredSum += indicator.actualTotal;
                    actualIndicatorForPredCount++;
                }
                if (indicator.plannedTotal) {
                    plannedIndicatorForPredSum += indicator.plannedTotal;
                    plannedIndicatorForPredCount++;
                }
            }

            const combinedIndicator = !isNaN(indicator.actualTotal) ? indicator.actualTotal : indicator.plannedTotal;
            if (!isNaN(combinedIndicator))
                combinedIndicatorSum += combinedIndicator;

            plannedShiftSum += plannedShifts;
            actualShiftSum += actualShifts;
            assignedShiftSum += assignedShifts;
            freeShiftSum += freeShifts;

            actualHourSum += workTime.actual;
            relevantActualHourSum += this.calculateRelevantShiftValue(mode, ( data.actualShifts || [] ), chartSettings, date, dateTo, mode === 'month' && date.format('YYYY.MM.DD') === '2023.06.07' ? 'actual' : null);
            assignedHourSum += workTime.assigned;
            relevantAssignedHourSum += this.calculateRelevantShiftValue(mode, ( data.assignedShifts || [] ), chartSettings, date, dateTo);
            plannedHourSum += workTime.planned;
            freeHourSum += workTime.free;

            let unitDisplay;
            if (mode === 'week')
                unitDisplay = date.format(DISPLAY_FORMATTERS[mode]) + ' - ' + this.moment(date).add(INTERVAL_MAP.week, UNIT_MAP.week).format(DISPLAY_FORMATTERS.weekTo);
            else if (mode === 'monthHourly')
                unitDisplay = date.format(DISPLAY_FORMATTERS.monthHourlyDetail);
            else
                unitDisplay = date.format(DISPLAY_FORMATTERS[mode]);

            statsPerUnit[key] = {
                date: this.moment(date),
                unitKey: key,
                unitDisplay,
                actualIndicatorSum: indicator.actualTotal,
                plannedIndicatorSum: indicator.plannedTotal,
                combinedIndicatorSum: combinedIndicator,
                actualMeoValue: meo.actual,
                plannedMeoValue: meo.planned,
                combinedMeoValue: meo.combined,
                actualVlhValue: vlh.actual,
                plannedVlhValue: vlh.planned,
                combinedVlhValue: vlh.combined,
                workTimeSums: workTime,
                utilization: div(assignedShifts, plannedShifts),
                coverage: div(actualShifts, assignedShifts),
                prediction: sub(1, div(indicator.plannedTotal, indicator.actualTotal)),
                productivity: {
                    actual: productivity.actual,
                    actualMax: productivity.actual,
                    actualMin: productivity.actual,
                    planned: productivity.planned,
                    combined: productivity.actual || productivity.planned,
                    deviation: sub(1, div(productivity.planned, productivity.actual)),
                },
                feedbackAvg: mode === 'month' ? feedback : null,
                feedbackCoverage: div(feedbackCount, actualShifts),
                recommendedEmployees: {
                    actual: actualRecommendedEmployees,
                    planned: plannedRecommendedEmployees,
                    combined: combinedRecommendedEmployees,
                },
                accuracy: {
                    actual: sub(1, div(( actualOverstaffing + actualUnderstaffing ), actualRecommendedEmployees)),
                    planned: sub(1, div(( plannedOverstaffing + plannedUnderstaffing ), plannedRecommendedEmployees)),
                    combined: sub(1, div(( combinedOverstaffing + combinedUnderstaffing ), combinedRecommendedEmployees)),
                },
                overstaffingPercent: {
                    actual: div(actualOverstaffing, actualRecommendedEmployees),
                    planned: div(plannedOverstaffing, plannedRecommendedEmployees),
                    combined: div(combinedOverstaffing, combinedRecommendedEmployees),
                },
                overstaffing: {
                    actual: actualOverstaffing,
                    planned: plannedOverstaffing,
                    combined: combinedOverstaffing,
                },
                understaffingPercent: {
                    actual: div(actualUnderstaffing, actualRecommendedEmployees),
                    planned: div(plannedUnderstaffing, plannedRecommendedEmployees),
                    combined: div(combinedUnderstaffing, combinedRecommendedEmployees),
                },
                understaffing: {
                    actual: actualUnderstaffing,
                    planned: plannedUnderstaffing,
                    combined: combinedUnderstaffing,
                },
                marketEmployeeCounts,
                originalWorkTimeStats: additionalData.originalWorkTimeStats,
                variableWorkTimeStats: additionalData.variableWorkTimeStats,
            };
        }

        const utilizationPercent = div(assignedHourSum, plannedHourSum);
        const coveragePercent = div(relevantActualHourSum, relevantAssignedHourSum);
        const predictionPercent = sub(1, div(plannedIndicatorForPredSum, actualIndicatorForPredSum) * div(actualIndicatorForPredCount, plannedIndicatorForPredCount));

        const combinedOverstaffingPercent = div(combinedOverstaffingSum, combinedRecommendedSum);
        const combinedUnderstaffingPercent = div(combinedUnderstaffingSum, combinedRecommendedSum);
        const combinedAccuracyPercent = sub(1, div(( combinedOverstaffingSum + combinedUnderstaffingSum ), combinedRecommendedSum));

        const actualOverstaffingPercent = div(actualOverstaffingSum, actualRecommendedSum);
        const actualUnderstaffingPercent = div(actualUnderstaffingSum, actualRecommendedSum);
        const actualAccuracyPercent = sub(1, div(( actualOverstaffingSum + actualUnderstaffingSum ), actualRecommendedSum));

        const plannedOverstaffingPercent = div(plannedOverstaffingSum, plannedRecommendedSum);
        const plannedUnderstaffingPercent = div(plannedUnderstaffingSum, plannedRecommendedSum);
        const plannedAccuracyPercent = sub(1, div(( plannedOverstaffingSum + plannedUnderstaffingSum ), plannedRecommendedSum));

        const actualProductivityAvg = div(actualProductivitySum, actualProductivityCount);
        const plannedProductivityAvg = div(plannedProductivitySum, plannedProductivityCount);
        const combinedProductivityAvg = div(combinedProductivitySum, combinedProductivityCount);

        const workTimeSums = {
            actual: actualHourSum,
            planned: plannedHourSum,
            assigned: assignedHourSum,
            free: freeHourSum,
        };

        if (!actualShiftsUntil.isBetween(from, to)) actualShiftsUntil = null;
        if (!plannedShiftsUntil.isBetween(from, to)) plannedShiftsUntil = null;
        if (!feedbackUntil.isBetween(from, to)) feedbackUntil = null;
        if (!predictionUntil.isBetween(from, to)) predictionUntil = null;
        if (!actualIndicatorUntil.isBetween(from, to)) actualIndicatorUntil = null;
        if (!plannedIndicatorUntil.isBetween(from, to)) plannedIndicatorUntil = null;

        const stats = {
            mode,
            from: this.moment(from),
            to: this.moment(to),
            actualIndicatorSum,
            plannedIndicatorSum,
            combinedIndicatorSum,
            actualIndicatorUntil,
            plannedIndicatorUntil,
            actualShiftsUntil,
            plannedShiftsUntil,
            accuracy: {
                actual: actualAccuracyPercent,
                planned: plannedAccuracyPercent,
                combined: combinedAccuracyPercent,
            },
            overstaffingPercent: {
                actual: actualOverstaffingPercent,
                planned: plannedOverstaffingPercent,
                combined: combinedOverstaffingPercent,
            },
            overstaffing: {
                actual: actualOverstaffingSum,
                planned: plannedOverstaffingSum,
                combined: combinedOverstaffingSum,
            },
            understaffingPercent: {
                actual: actualUnderstaffingPercent,
                planned: plannedUnderstaffingPercent,
                combined: combinedUnderstaffingPercent,
            },
            understaffing: {
                actual: actualUnderstaffingSum,
                planned: plannedUnderstaffingSum,
                combined: combinedUnderstaffingSum,
            },
            productivity: {
                actual: actualProductivityAvg,
                actualMax: actualProductivityMax,
                actualMin: actualProductivityMin,
                planned: plannedProductivityAvg,
                combined: combinedProductivityAvg,
                deviation: sub(1, div(plannedProductivityAvg, actualProductivityAvg)),
            },
            utilization: utilizationPercent,
            coverage: coveragePercent,
            prediction: predictionPercent,
            predictionUntil,
            feedbackAvg: div(feedbackSum, feedbackCount),
            feedbackUntil,
            feedbackCoverage: div(feedbackCount, actualShiftSum),
            workTimeSums,
            perUnit: statsPerUnit,
            originalWorkTimeStats: additionalData.originalWorkTimeStats,
            variableWorkTimeStats: additionalData.variableWorkTimeStats,
            additionalData,
        };
        // console.log(stats);
        return stats;
    }

    /**
     * Calculates the stats of the given months
     * @param dailySumStats {{
     *     date: string,
     *     plannedWorkTime: number,
     *     assignedPlannedWorkTime: number,
     *     plannedWorkTimeCrew: number,
     *     assignedPlannedWorkTimeCrew: number,
     *     planShiftCount: number,
     *     assignedPlanShiftCount: number,
     *     actualWorkTime: number,
     *     actualWorkTimeCrew: number,
     *     actualShiftFeedback: number,
     *     actualShiftCount: number,
     *     plannedSales: number,
     *     actualSales: number,
     *     plannedNetSales: number,
     *     actualNetSales: number,
     *     plannedGuests: number,
     *     actualGuests: number,
     * }[]}
     * employeeLevel 1 == crew, 2 == mgmt
     * @param monthlyRegularWorkTime {{
     *   employeeLevel: 1 | 2,
     *   from: string,
     *   to: string,
     *   workTimeRegular: number,
     * }[]}
     * @param indicatorCategory { 1 | 2 | 3 } Category 1 == sales, 2 == net sales, 3 == guests
     * @param employeeLevel { null | 1 | 2 } if null, all positions will be used
     */
    calculatePastStats(dailySumStats, monthlyRegularWorkTime, indicatorCategory, employeeLevel = null) {

        // Sum monthly stats

        /** @type {{ [monthKey: string]: {
         *    plannedSales: number, actualSales: number,
         *    plannedNetSales: number, actualNetSales: number,
         *    plannedGuests: number, actualGuests: number,
         * }} } */
        const monthlyIndicatorSums = {};

        const bigIntDivisor = Math.pow(10, 7);

        for (const stats of dailySumStats) {
            const date = this.moment(stats.date);
            const key = date.format('YYYY-MM');
            const indicatorSums = monthlyIndicatorSums[key] || {
                plannedSales: 0, actualSales: 0,
                plannedNetSales: 0, actualNetSales: 0,
                plannedGuests: 0, actualGuests: 0,
            };
            indicatorSums.plannedSales += div(stats.plannedSales, bigIntDivisor) || 0;
            indicatorSums.actualSales += div(stats.actualSales, bigIntDivisor) || 0;
            indicatorSums.plannedNetSales += div(stats.plannedNetSales, bigIntDivisor) || 0;
            indicatorSums.actualNetSales += div(stats.actualNetSales, bigIntDivisor) || 0;
            indicatorSums.plannedGuests += div(stats.plannedGuests, bigIntDivisor) || 0;
            indicatorSums.actualGuests += div(stats.actualGuests, bigIntDivisor) || 0;
            monthlyIndicatorSums[key] = indicatorSums;
        }

        // Sum regular work time

        /** @type {{ [monthKey: string]: { combined: number, crew: number, mgmt: number } }} */
        const regularWorkTimeMap = {};

        for (const entry of monthlyRegularWorkTime) {
            const fromDate = this.moment(entry.from);
            const key = fromDate.format('YYYY-MM');
            const workTimes = regularWorkTimeMap[key] || { combined: 0, crew: 0, mgmt: 0 };
            workTimes.combined += entry.workTimeRegular;
            switch (entry.employeeLevel) {
                case 1:
                    workTimes.crew += entry.workTimeRegular;
                    break;
                case 2:
                    workTimes.mgmt += entry.workTimeRegular;
                    break;
            }
            regularWorkTimeMap[key] = workTimes;
        }

        // Calculate recommended employee curve for stat calculation

        let plannedHourSum = 0;
        let actualHourSum = 0;
        let assignedHourSum = 0;
        let actualIndicatorSum = 0;
        let plannedIndicatorSum = 0;
        let combinedIndicatorSum = 0;
        let actualIndicatorCount = 0;
        let plannedIndicatorCount = 0;
        let combinedOverstaffingSum = 0;
        let combinedUnderstaffingSum = 0;
        let combinedRecommendedSum = 0;
        let actualOverstaffingSum = 0;
        let actualUnderstaffingSum = 0;
        let actualRecommendedSum = 0;
        let plannedOverstaffingSum = 0;
        let plannedUnderstaffingSum = 0;
        let plannedRecommendedSum = 0;
        let actualProductivitySum = 0;
        let actualProductivityCount = 0;
        let plannedProductivitySum = 0;
        let plannedProductivityCount = 0;
        let combinedProductivitySum = 0;
        let combinedProductivityCount = 0;
        let feedbackSum = 0;
        let feedbackCount = 0;

        for (let stats of dailySumStats) {
            stats = { ...stats };
            const date = this.moment(stats.date);
            const key = date.format('YYYY-MM-DD');
            const monthKey = date.format('YYYY-MM');

            const statForCategory = { planned: null, actual: null, combined: null };
            const monthlyStatSumForIndicatorCategory = { planned: null, actual: null, combined: null };
            const monthlyIndicatorSum = monthlyIndicatorSums[monthKey];
            if (indicatorCategory === 1) {
                statForCategory.actual = stats.actualSales;
                statForCategory.planned = stats.plannedSales;
                statForCategory.combined = stats.actualSales || stats.plannedSales;
                monthlyStatSumForIndicatorCategory.actual = monthlyIndicatorSum.actualSales;
                monthlyStatSumForIndicatorCategory.planned = monthlyIndicatorSum.plannedSales;
                monthlyStatSumForIndicatorCategory.combined = monthlyIndicatorSum.actualSales || monthlyIndicatorSum.plannedSales;
            } else if (indicatorCategory === 2) {
                statForCategory.actual = stats.actualNetSales;
                statForCategory.planned = stats.plannedNetSales;
                statForCategory.combined = stats.actualNetSales || stats.plannedNetSales;
                monthlyStatSumForIndicatorCategory.actual = monthlyIndicatorSum.actualNetSales;
                monthlyStatSumForIndicatorCategory.planned = monthlyIndicatorSum.plannedNetSales;
                monthlyStatSumForIndicatorCategory.combined = monthlyIndicatorSum.actualNetSales || monthlyIndicatorSum.plannedNetSales;
            } else if (indicatorCategory === 3) {
                statForCategory.actual = stats.actualGuests;
                statForCategory.planned = stats.plannedGuests;
                statForCategory.combined = stats.actualGuests || stats.plannedGuests;
                monthlyStatSumForIndicatorCategory.actual = monthlyIndicatorSum.actualGuests;
                monthlyStatSumForIndicatorCategory.planned = monthlyIndicatorSum.plannedGuests;
                monthlyStatSumForIndicatorCategory.combined = monthlyIndicatorSum.actualGuests || monthlyIndicatorSum.plannedGuests;
            }

            statForCategory.actual = div(statForCategory.actual, bigIntDivisor) || 0;
            statForCategory.planned = div(statForCategory.planned, bigIntDivisor) || 0;
            statForCategory.combined = div(statForCategory.combined, bigIntDivisor) || 0;

            let regularWorkTimeHours = 0;
            if (employeeLevel === null) {
                regularWorkTimeHours = regularWorkTimeMap[monthKey].combined;
            } else if (employeeLevel === 1) {
                regularWorkTimeHours = regularWorkTimeMap[monthKey].crew;
            } else if (employeeLevel === 2) {
                regularWorkTimeHours = regularWorkTimeMap[monthKey].mgmt;
            }
            regularWorkTimeHours /= ( 60 * 60 ); // to get hours (orig. data is in seconds)

            const actualRecommendedEmployeeCount = monthlyStatSumForIndicatorCategory.actual === 0
                ? null
                : regularWorkTimeHours * ( statForCategory.actual / monthlyStatSumForIndicatorCategory.actual )

            const plannedRecommendedEmployeeCount = monthlyStatSumForIndicatorCategory.planned === 0
                ? null
                : regularWorkTimeHours * ( statForCategory.planned / monthlyStatSumForIndicatorCategory.planned )

            const combinedRecommendedEmployeeCount = monthlyStatSumForIndicatorCategory.combined === 0
                ? null
                : regularWorkTimeHours * ( statForCategory.combined / monthlyStatSumForIndicatorCategory.combined )

            // Calculate accuracy stats

            let combinedOverstaffing = 0;
            let combinedUnderstaffing = 0;
            if (stats.assignedPlanShiftCount > combinedRecommendedEmployeeCount)
                combinedOverstaffing = stats.assignedPlanShiftCount - combinedRecommendedEmployeeCount;
            else if (stats.assignedPlanShiftCount < combinedRecommendedEmployeeCount)
                combinedUnderstaffing = combinedRecommendedEmployeeCount - stats.assignedPlanShiftCount;

            combinedOverstaffingSum += combinedOverstaffing;
            combinedUnderstaffingSum += combinedUnderstaffing;
            combinedRecommendedSum += combinedRecommendedEmployeeCount;

            let actualOverstaffing = 0;
            let actualUnderstaffing = 0;
            if (stats.assignedPlanShiftCount > actualRecommendedEmployeeCount)
                actualOverstaffing = stats.assignedPlanShiftCount - actualRecommendedEmployeeCount;
            else if (stats.assignedPlanShiftCount < actualRecommendedEmployeeCount)
                actualUnderstaffing = actualRecommendedEmployeeCount - stats.assignedPlanShiftCount;

            let plannedOverstaffing = 0;
            let plannedUnderstaffing = 0;
            if (stats.assignedPlanShiftCount > plannedRecommendedEmployeeCount)
                plannedOverstaffing = stats.assignedPlanShiftCount - plannedRecommendedEmployeeCount;
            else if (stats.assignedPlanShiftCount < plannedRecommendedEmployeeCount)
                plannedUnderstaffing = plannedRecommendedEmployeeCount - stats.assignedPlanShiftCount;

            // Sum individual stats to calculate combined stats

            stats.plannedWorkTime = div(stats.plannedWorkTime, 3600);
            stats.actualWorkTime = div(stats.actualWorkTime, 3600);
            stats.assignedPlannedWorkTime = div(stats.assignedPlannedWorkTime, 3600);

            if (employeeLevel === null) {
                plannedHourSum += stats.plannedWorkTime;
                actualHourSum += stats.actualWorkTime;
                assignedHourSum += stats.assignedPlannedWorkTime;
            } else if (employeeLevel === 1) {
                plannedHourSum += stats.plannedWorkTimeCrew;
                actualHourSum += stats.actualWorkTimeCrew;
                assignedHourSum += stats.assignedPlannedWorkTimeCrew;
            } else if (employeeLevel === 2) {
                plannedHourSum += stats.plannedWorkTime - stats.plannedWorkTimeCrew;
                actualHourSum += stats.actualWorkTime - stats.actualWorkTimeCrew;
                assignedHourSum += stats.assignedPlannedWorkTime - stats.assignedPlannedWorkTimeCrew;
            }

            actualIndicatorSum += statForCategory.actual;
            plannedIndicatorSum += statForCategory.planned;
            combinedIndicatorSum += statForCategory.combined;
            actualIndicatorCount += statForCategory.actual ? 1 : 0;
            plannedIndicatorCount += statForCategory.planned ? 1 : 0;

            actualOverstaffingSum += actualOverstaffing;
            actualUnderstaffingSum += actualUnderstaffing;
            actualRecommendedSum += actualRecommendedEmployeeCount;

            plannedOverstaffingSum += plannedOverstaffing;
            plannedUnderstaffingSum += plannedUnderstaffing;
            plannedRecommendedSum += plannedRecommendedEmployeeCount;

            combinedOverstaffingSum += combinedOverstaffing;
            combinedUnderstaffingSum += combinedUnderstaffing;
            combinedRecommendedSum += combinedRecommendedEmployeeCount;

            // Calculate productivity
            const actualProductivity = div(statForCategory.actual, stats.assignedPlannedWorkTime);
            const plannedProductivity = div(statForCategory.planned, stats.assignedPlannedWorkTime);
            const combinedProductivity = div(statForCategory.combined, stats.assignedPlannedWorkTime);

            actualProductivitySum += actualProductivity;
            actualProductivityCount += actualProductivity ? 1 : 0;
            plannedProductivitySum += plannedProductivity;
            plannedProductivityCount += plannedProductivity ? 1 : 0;
            combinedProductivitySum += combinedProductivity;
            combinedProductivityCount += combinedProductivity ? 1 : 0;

            feedbackSum += stats.actualShiftFeedback;
            feedbackCount += stats.actualShiftFeedback ? 1 : 0;
        }

        // Calculate stats

        const utilizationPercent = div(assignedHourSum, plannedHourSum);
        const coveragePercent = div(actualHourSum, assignedHourSum);
        const predictionPercent = sub(1, div(plannedIndicatorSum, actualIndicatorSum)) * div(actualIndicatorCount, plannedIndicatorCount);

        const combinedOverstaffingPercent = div(combinedOverstaffingSum, combinedRecommendedSum);
        const combinedUnderstaffingPercent = div(combinedUnderstaffingSum, combinedRecommendedSum);
        const combinedAccuracyPercent = sub(1, div(( combinedOverstaffingSum + combinedUnderstaffingSum ), combinedRecommendedSum));

        const actualOverstaffingPercent = div(actualOverstaffingSum, actualRecommendedSum);
        const actualUnderstaffingPercent = div(actualUnderstaffingSum, actualRecommendedSum);
        const actualAccuracyPercent = sub(1, div(( actualOverstaffingSum + actualUnderstaffingSum ), actualRecommendedSum));

        const plannedOverstaffingPercent = div(plannedOverstaffingSum, plannedRecommendedSum);
        const plannedUnderstaffingPercent = div(plannedUnderstaffingSum, plannedRecommendedSum);
        const plannedAccuracyPercent = sub(1, div(( plannedOverstaffingSum + plannedUnderstaffingSum ), plannedRecommendedSum));

        const actualProductivityAvg = div(actualProductivitySum, actualProductivityCount);
        const plannedProductivityAvg = div(plannedProductivitySum, plannedProductivityCount);
        const combinedProductivityAvg = div(combinedProductivitySum, combinedProductivityCount);

        return {
            actualIndicatorSum,
            plannedIndicatorSum,
            combinedIndicatorSum,
            accuracy: {
                actual: actualAccuracyPercent,
                planned: plannedAccuracyPercent,
                combined: combinedAccuracyPercent,
            },
            overstaffingPercent: {
                actual: actualOverstaffingPercent,
                planned: plannedOverstaffingPercent,
                combined: combinedOverstaffingPercent,
            },
            overstaffing: {
                actual: actualOverstaffingSum,
                planned: plannedOverstaffingSum,
                combined: combinedOverstaffingSum,
            },
            understaffingPercent: {
                actual: actualUnderstaffingPercent,
                planned: plannedUnderstaffingPercent,
                combined: combinedUnderstaffingPercent,
            },
            understaffing: {
                actual: actualUnderstaffingSum,
                planned: plannedUnderstaffingSum,
                combined: combinedUnderstaffingSum,
            },
            productivity: {
                actual: actualProductivityAvg,
                planned: plannedProductivityAvg,
                combined: combinedProductivityAvg,
            },
            utilization: utilizationPercent,
            coverage: coveragePercent,
            prediction: predictionPercent,
            feedbackAvg: div(feedbackSum, feedbackCount),
            workTimeSums: {
                actual: actualHourSum,
                planned: plannedHourSum,
                assigned: assignedHourSum,
                free: plannedHourSum - assignedHourSum,
            },
        };
    }


    /******************************************************************************/
// Chart Data calculation functions                                           */
    /******************************************************************************/

    /**
     * Combines the given maps to a single map, which can then be used to calculate the displayable data.
     * @param maps {{
     *     indicatorMap: Object,
     *     actualShiftMap: Object,
     *     planShiftMap: Object,
     *     freeShiftMap: Object,
     *     assignedShiftMap: Object,
     *     actualRecommendedEmployeeCountMap: Object,
     *     plannedRecommendedEmployeeCountMap: Object,
     *     recommendedEmployeeCountMap: Object,
     *     marketEmployeeCountMap: Object,
     *     workTimeMap: Object,
     *     productivityMap: Object,
     *     productiveWorkTimeMap: Object,
     *     feedbackMap: Object,
     *     meoMap: Object,
     *     vlhMap: Object,
     *     additionalData: Object,
     *  }}
     * @param renderFrom {Moment}
     * @param renderTo {Moment}
     * @param fromDate {Moment}
     * @param toDate {Moment}
     * @param mode {'day'|'week'|'month'|'monthHourly'}
     */
    combineMaps(maps, renderFrom, renderTo, fromDate, toDate, mode) {
        const {
            indicatorMap,
            actualShiftMap,
            planShiftMap,
            freeShiftMap,
            assignedShiftMap,
            actualRecommendedEmployeeCountMap,
            plannedRecommendedEmployeeCountMap,
            recommendedEmployeeCountMap,
            marketEmployeeCountMap,
            workTimeMap,
            productivityMap,
            productiveWorkTimeMap,
            feedbackMap,
            meoMap,
            vlhMap,
            additionalData,
        } = maps;

        const combined = {};

        const unit = UNIT_MAP[mode];
        const interval = INTERVAL_MAP[mode];
        // combine data into single object from start to end date to allow market data in trunc weeks
        for (const date = this.moment(renderFrom); date.isBefore(renderTo); date.add(interval, unit)) {
            const key = date.format(KEY_FORMATTERS[mode]);

            if (date.isBetween(fromDate, toDate, null, '[]')) {
                const feedbackAvg = feedbackMap ? feedbackMap[key] : 0;
                combined[key] = {
                    date: this.moment(date),
                    indicator: indicatorMap[key],
                    actualShifts: actualShiftMap[key] || [],
                    planShifts: planShiftMap[key] || [],
                    freeShifts: freeShiftMap[key] || [],
                    assignedShifts: assignedShiftMap[key] || [],
                    recommendedEmployeeCount: {
                        actual: actualRecommendedEmployeeCountMap[key],
                        planned: plannedRecommendedEmployeeCountMap[key],
                        combined: recommendedEmployeeCountMap[key],
                    },
                    meoValue: meoMap[key],
                    vlhValue: vlhMap ? vlhMap[key] : null,
                    marketEmployeeCount: marketEmployeeCountMap[key],
                    workTime: workTimeMap[key],
                    productivity: productivityMap[key],
                    feedbackAvg: feedbackAvg || 0,
                    productiveWorkTime: productiveWorkTimeMap[key],
                };
            } else {
                combined[key] = {
                    date: this.moment(date),
                    indicator: null,
                    actualShifts: null,
                    planShifts: null,
                    freeShifts: null,
                    assignedShifts: null,
                    recommendedEmployeeCount: null,
                    marketEmployeeCount: marketEmployeeCountMap[key],
                    workTime: null,
                    productivity: null,
                    productiveWorkTime: null,
                    meoValue: null,
                    vlhValue: null,
                    feedbackAvg: 0,
                };
            }
        }

        return { ...combined, additionalData };
    }


    /**
     * Calculates the raw data that is used to generate the chart data and can be cached since it's bound to a specific day/week/month.
     * The resulting data could be cached for improved performance, or some of the arguments
     * @param employeeMap
     * @param workTimeRegular
     * @param employeeWorkBalanceMap
     * @param sumStatsForMonth
     * @param sumStats
     * @param indicators
     * @param indicatorTypeMap
     * @param plannedShifts
     * @param actualShifts
     * @param actualBreaks
     * @param marketData
     * @param month
     * @param renderFrom
     * @param renderTo
     * @param chartSettings
     * @param mode
     * @param meoData
     * @param vlhData
     * @param monthlyIndicatorSums
     * @param spotMap
     * @returns {Promise<Object>}
     */
    async calculateRawChartData(employeeMap, workTimeRegular, employeeWorkBalanceMap, sumStatsForMonth, indicators, indicatorTypeMap,
                                plannedShifts, actualShifts, actualBreaks, marketData, month, renderFrom, renderTo,
                                chartSettings, mode, meoData = null, vlhData = null, monthlyIndicatorSums = {},
                                spotMap, sumStats) {
        if (!Array.isArray(plannedShifts))
            plannedShifts = Object.values(plannedShifts);
        if (!Array.isArray(actualShifts))
            actualShifts = Object.values(actualShifts);

        // Trim from and to dates to the first and last day of the month, in order to create accurate data without trunc weeks
        let fromDate = this.moment(renderFrom);
        if (( mode === 'month' || mode === 'monthHourly' ) && renderFrom.isBefore(month))
            fromDate = this.moment(month);
        let toDate = this.moment(renderTo);
        if (( mode === 'month' || mode === 'monthHourly' ) && renderTo.isAfter(this.moment(month).endOf('month').add(1, 'day').startOf('day'))) //END OF MONTH == START OF NEXT MONTH!
            toDate = this.moment(month).endOf('month').add(1, 'day').startOf('day');

        const employeeIds = this.filterEmployeeIds(employeeMap, chartSettings.selectedEmployeeLevels || [], spotMap);

        // Maps from which the data is calculated
        let indicatorMap = this.filterIndicators(indicators, fromDate, toDate, mode, chartSettings.selectedIndicatorTypeId, 'combined');
        indicatorMap = this.convertIndicatorsToFractions(indicatorMap, indicatorTypeMap);
        let planShiftMap = this.filterShifts(plannedShifts, fromDate, toDate, mode, employeeIds);
        let freeShiftMap = this.filterShifts(plannedShifts.filter(s => !s.employeeId), fromDate, toDate, mode);
        let assignedShiftMap = this.filterShifts(plannedShifts.filter(s => !!s.employeeId), fromDate, toDate, mode, employeeIds);
        actualShifts = this.combineActualShiftBreaks(actualShifts, actualBreaks);
        let actualShiftMap = this.filterShifts(actualShifts, fromDate, toDate, mode, employeeIds);
        if (mode === 'week') {
            indicatorMap = this.combineIndicators(indicatorMap, mode, INTERVAL_MAP[mode]);
            planShiftMap = this.combineShifts(planShiftMap, fromDate, toDate, mode, INTERVAL_MAP[mode]);
            assignedShiftMap = this.combineShifts(assignedShiftMap, fromDate, toDate, mode, INTERVAL_MAP[mode]);
            freeShiftMap = this.combineShifts(freeShiftMap, fromDate, toDate, mode, INTERVAL_MAP[mode]);
            actualShiftMap = this.combineShifts(actualShiftMap, fromDate, toDate, mode, INTERVAL_MAP[mode]);
            marketData = this.combineMarketData(marketData, fromDate, toDate, mode, INTERVAL_MAP[mode]);
        }

        // Sums of the maps; used to calculate limits and derived percentages
        const [ combinedIndicatorSum, combinedIndicatorPercent ] = this.calculateIndicatorSum(
            indicatorMap,
            fromDate,
            toDate,
            'combined',
            mode,
        );
        const [ actualIndicatorSum, actualIndicatorPercent ] = this.calculateIndicatorSum(
            indicatorMap,
            fromDate,
            toDate,
            'actual',
            mode,
        );
        const [ plannedIndicatorSum, plannedIndicatorPercent ] = this.calculateIndicatorSum(
            indicatorMap,
            fromDate,
            toDate,
            'planned',
            mode,
        );

        

        const originalWorkTimeHours = this.calculateRegularWorkTime(workTimeRegular, chartSettings.selectedEmployeeLevels);
        const actualPreferenceHours = this.calculateUnproductiveWorkTime(sumStatsForMonth, chartSettings.selectedEmployeeLevels);

        const prevMonthBalanceSum = Object.values(employeeWorkBalanceMap)
            .filter(ewb => !!employeeMap[ewb.employeeId])
            .reduce((acc, ewb) => acc + ewb.workTimeBalance, 0);
        const prevMonthBalance = prevMonthBalanceSum / 60;

        const [ realAvailableWorkTimeHours, , absenceHours ] = this.calculateVariableWorkTimeStats(
            originalWorkTimeHours,
            prevMonthBalance,
            actualPreferenceHours,
            chartSettings.includePrevMonthTime,
            chartSettings.useExistingPreferences,
            chartSettings.absenceHoursPercentage,
        );

        const [ actualWorkTimeSum, actualBreakTimeSum, actualWorkTimeEntities ] = this.calculateWorkTime(actualShiftMap, fromDate, toDate, employeeIds, chartSettings.subtractBreaks);
        const [ plannedWorkTimeSum, plannedBreakTimeSum, plannedWorkTimeEntities ] = this.calculateWorkTime(planShiftMap, fromDate, toDate, employeeIds, chartSettings.subtractBreaks);
        // const [ assignedWorkTimeSum, assignedBreakTimeSum, assignedWorkTimeCount ] = this.calculateWorkTime(assignedShiftMap, fromDate, toDate, employeeIds, chartSettings.subtractBreaks);
        // const [ freeWorkTimeSum, freeBreakTimeSum, freeWorkTimeCount ] = this.calculateWorkTime(freeShiftMap, fromDate, toDate, employeeIds, chartSettings.subtractBreaks);

        // Calculate the divisor for the calculation of the recommended shift count
        let recShiftDivisor = 1 / ( UNIT_MAP[mode] === 'minutes' ? 60 : 1 );
        let marketDivisor = 1;

        if (mode === 'month' && !chartSettings.displayHours) {
            recShiftDivisor = marketDivisor = chartSettings.useMeanShiftLength
                ? ( actualWorkTimeSum + plannedWorkTimeSum ) / ( actualWorkTimeEntities + plannedWorkTimeEntities )
                : chartSettings.defaultShiftLength;
        }

        let meoMap = {};
        if (meoData) {
            // Filters spot ids based on the corresponding employee level
            const spotIds = Object.entries(spotMap)
                .filter(([ , spot ]) => chartSettings.selectedEmployeeLevels.includes(spot.employeeLevel))
                .map(([ id ]) => id);

            meoMap = this.calculateMEOData(meoData, fromDate, toDate, mode, 60 * marketDivisor, spotIds);
            if (mode === 'week' || mode === 'monthHourly')
                meoMap = this.combineMEOData(meoMap, fromDate, toDate, mode, INTERVAL_MAP[mode]);
        }

        let vlhMap = {};
        if (vlhData) {
            const spotIds = Object.entries(spotMap)
                .filter(([ , spot ]) => chartSettings.selectedEmployeeLevels.includes(spot.employeeLevel))
                .map(([ id ]) => id);

            vlhMap = this.calculateMEOData(vlhData, fromDate, toDate, mode, 60 * marketDivisor, spotIds);
            if (mode === 'week' || mode === 'monthHourly')
                vlhMap = this.combineMEOData(vlhMap, fromDate, toDate, mode, INTERVAL_MAP[mode]);
        }

        const [ indicatorPercentMap ] = this.calculateIndicatorPercent(indicatorMap, combinedIndicatorSum);
        const [ actualIndicatorPercentMap ] = this.calculateIndicatorPercent(indicatorMap, actualIndicatorSum, 'actual');
        const [ plannedIndicatorPercentMap ] = this.calculateIndicatorPercent(indicatorMap, plannedIndicatorSum, 'planned');

        let wtPercent = 1;
        let actualWTPercent = 1;
        let plannedWTPercent = 1;

        if (mode !== 'month') {
            wtPercent = monthlyIndicatorSums.combined ? combinedIndicatorSum / monthlyIndicatorSums.combined : 1;
            actualWTPercent = monthlyIndicatorSums.actual ? actualIndicatorSum / monthlyIndicatorSums.actual : 1;
            plannedWTPercent = monthlyIndicatorSums.planned ? plannedIndicatorSum / monthlyIndicatorSums.planned : 1;
        }

        const employeeWorkTimeMap = this.calculateEmployeeWorkTimeMap(indicatorPercentMap, realAvailableWorkTimeHours * wtPercent * ( mode === 'month' ? combinedIndicatorPercent : 1 ));
        const actualEmployeeWorkTimeMap = this.calculateEmployeeWorkTimeMap(actualIndicatorPercentMap, realAvailableWorkTimeHours * actualWTPercent * ( mode === 'month' ? actualIndicatorPercent : 1 ));
        const plannedEmployeeWorkTimeMap = this.calculateEmployeeWorkTimeMap(plannedIndicatorPercentMap, realAvailableWorkTimeHours * plannedWTPercent * ( mode === 'month' ? plannedIndicatorPercent : 1 ));

        const recommendedEmployeeCountMap = this.calculateRecommendedEmployeeCount(employeeWorkTimeMap, recShiftDivisor);
        const actualRecommendedEmployeeCountMap = this.calculateRecommendedEmployeeCount(actualEmployeeWorkTimeMap, recShiftDivisor);
        const plannedRecommendedEmployeeCountMap = this.calculateRecommendedEmployeeCount(plannedEmployeeWorkTimeMap, recShiftDivisor);
        const marketEmployeeCountMap = this.calculateMarketEmployeeCount(marketData, chartSettings, mode, marketDivisor);

        const workTimeMap = this.calculateAllWorkTimes(planShiftMap, actualShiftMap, mode, fromDate, toDate, employeeIds, chartSettings.subtractBreaks);
        /*
        // TO DO (?): Calculate workTimes per spot to be displayed in the detail view
        for (const [spotId, spotData] of Object.entries(spotMap)) {
          const employeeIdsForSpot = filterEmployeeIds(employeeMap, [spotId]);
          const workTimesForSpot = calculateAllWorkTimes(planShiftMap, actualShiftMap, mode, fromDate, toDate, employeeIdsForSpot, chartSettings.subtractBreaks);
          console.log(spotData.label, workTimesForSpot);
        }*/

        // TO DO (?): Calculate productivity with or without managers (includeMgmt)
        // Unnecessary, as the managers can be excluded in the spots filter
        const productiveWorkTimeMap = this.calculateProductiveWorkTime(planShiftMap, actualShiftMap, mode, fromDate, toDate, employeeIds, spotMap);
        const productivityMap = this.calculateProductivity(productiveWorkTimeMap, indicatorMap, 'day');

        const feedbackMap = this.mapEmployeeFeedback(sumStats, mode);

        const originalWorkTimeStats = {
            workTimeHours: originalWorkTimeHours,
            preferenceHours: actualPreferenceHours, // actual sum of preference hours for the selected month
            prevMonthBalance,
        };
        const variableWorkTimeStats = {
            workTimeHours: realAvailableWorkTimeHours,
            absenceHours, // either custom absence hours or actualPreferenceHours
            prevMonthBalance,
        };
        /*
        console.group('Worktime stats');

        console.log(`Calculating planned demand for regular worktime ${fromDate.format('DD.MM.YYYY')} - ${toDate.format('DD.MM.YYYY')}`);
        console.log('Worktime regular:', originalWorkTimeHours * 60 * 60, 'sec. |', originalWorkTimeHours, 'h');
        console.log('Employee preferences:', actualPreferenceHours * 60 * 60, 'sec. |', actualPreferenceHours, 'h');
        console.log('Prev month balance:', prevMonthBalance * 60 * 60, 'sec. |', prevMonthBalance, 'h');
        console.log('Worktime available:', realAvailableWorkTimeHours * 60 * 60, 'sec. |', realAvailableWorkTimeHours, 'h');
        console.log('Actual worktime:', actualWorkTimeSum + ( chartSettings.subtractBreaks ? actualBreakTimeSum : 0 ), 'h', 'Breaks:', actualBreakTimeSum, 'h', 'Remaining:', actualWorkTimeSum, 'h');
        console.log('Planned worktime:', plannedWorkTimeSum + ( chartSettings.subtractBreaks ? plannedBreakTimeSum : 0 ), 'h', 'Breaks:', plannedBreakTimeSum, 'h', 'Remaining:', plannedWorkTimeSum, 'h');
        const indics = Object.entries(indicatorMap);
        console.log('Using', indics.reduce((acc, val) => val.length + acc, 0), 'indicators on', indics.length, 'days');
        if (indics.length > 0)
            console.log(`Got sales values for ${this.moment(indics[0][0]).format('DD.MM.YYYY')} - ${this.moment(indics[indics.length - 1][0]).format('DD.MM.YYYY')}`);
        console.log('Total sales (planned + actual):', combinedIndicatorSum);
        console.log('Total actual sales:', actualIndicatorSum);
        console.log('Total planned sales:', plannedIndicatorSum);
        // console.log('--------------------------------------------------------------------------------');

        for (const [ key, indicators ] of indics) {
            const date = this.moment(indicators.from);
            console.groupCollapsed(date.format('DD.MM.YYYY'));
            console.log('Actual Sales:', indicators.actualTotal);
            console.log('Planned Sales:', indicators.plannedTotal);
            console.log('Actual WorkTime:', workTimeMap[key].actualWorkTime, 'h', 'Breaks:', workTimeMap[key].actualBreak, 'h', 'Remaining:', workTimeMap[key].actual, 'h');
            console.log('Planned WorkTime:', workTimeMap[key].plannedWorkTime, 'h', 'Breaks:', workTimeMap[key].plannedBreak, 'h', 'Remaining:', workTimeMap[key].planned, 'h');
            console.log('Assigned WorkTime:', workTimeMap[key].assignedWorkTime, 'h', 'Breaks:', workTimeMap[key].assignedBreak, 'h', 'Remaining:', workTimeMap[key].assigned, 'h');
            console.log('Free WorkTime:', workTimeMap[key].freeWorkTime, 'h', 'Breaks:', workTimeMap[key].freeBreak, 'h', 'Remaining:', workTimeMap[key].free, 'h');
            console.log('Demand:', employeeWorkTimeMap[key], 'h |', recommendedEmployeeCountMap[key], 'Schichten');
            console.log('Actual Demand:', actualEmployeeWorkTimeMap[key], 'h |', actualRecommendedEmployeeCountMap[key], 'Schichten');
            console.log('Planned Demand:', plannedEmployeeWorkTimeMap[key], 'h |', plannedRecommendedEmployeeCountMap[key], 'Schichten');
            // console.log(indicators);
            // console.log('--------------------------------------------------------------------------------');
            console.groupEnd();
        }
        console.groupEnd(); */

        const dataMaps = {
            indicatorMap,
            actualShiftMap,
            planShiftMap,
            assignedShiftMap,
            freeShiftMap,
            actualRecommendedEmployeeCountMap,
            plannedRecommendedEmployeeCountMap,
            recommendedEmployeeCountMap,
            meoMap,
            vlhMap,
            marketEmployeeCountMap,
            workTimeMap,
            productivityMap,
            productiveWorkTimeMap,
            feedbackMap,
            additionalData: {
                combinedIndicatorPercent,
                actualIndicatorPercent,
                plannedIndicatorPercent,
                indicatorPercentMap,
                actualIndicatorPercentMap,
                plannedIndicatorPercentMap,
                wtPercent,
                actualWTPercent,
                plannedWTPercent,
                plannedWorkTimeSum,
                plannedWorkTimeEntities,
                originalWorkTimeStats,
                variableWorkTimeStats,
            },
        };
        const rawData = this.combineMaps(dataMaps, renderFrom, renderTo, fromDate, toDate, mode);

        const stats = this.calculateStats(rawData, renderFrom, renderTo, mode, chartSettings);

        return [ rawData, stats, dataMaps ];
    }


// TODO (?): Cut bar values > 120%
    calculateDisplayableChartData(rawChartData, stats, renderFrom, renderTo, mode, chartSettings, indicatorTypeMap = {}) {
        const displayData = new Map();

        const wantedDisplayData = Object.entries(chartSettings.displayData || {});
        if (wantedDisplayData.length > 0) {
            for (const [ displayKey, displayValue ] of wantedDisplayData) {
                if (displayValue) {
                    displayData.set(displayKey, displayValue);
                }
            }
        } else {
            displayData.set('lines', [ 'actual', 'planned', 'market' ]);
            displayData.set('bars', [ 'actual', 'assigned', 'overstaffed', 'free' ]);
            displayData.set('selectedStat', null);
        }

        const chartData = {
            orangeValues: [],
            greenValues: [],
            redValues: [],
            redAltValues: [],
            grayValues: [],
            lineValues: [],
            predictionLineValues: [],
            yellowLineValues: [],
            redLineValues: [],
            blueLineValues: [],
            blueAreaValues: [],
            datasetLabels: {},
            datasetColors: {
                orange: '#e86e13',
                green: '#879d53',
            },
            barLabels: [],
            barTitles: [],
            barLabelColors: [],
            barIcons: [],
            barTooltips: [],
            separators: [],
            horizontalLines: undefined,
            predictionStart: Object.keys(rawChartData).length,
            currentDayIndex: null,
            lineDotSize: 0,
            padding: {
                top: 17,
            },
        };

        const calculationMode = chartSettings.calculationMode; // Determines whether to use meo, vlh or demand (recommended) values
        const barsToDisplay = displayData.get('bars');
        const linesToDisplay = displayData.get('lines');

        let filter = '';
        if (linesToDisplay.includes('actual') && linesToDisplay.includes('planned')) {
            filter = 'combined';
        } else if (linesToDisplay.includes('actual')) {
            filter = 'actual';
        } else if (linesToDisplay.includes('planned')) {
            filter = 'planned';
        }
        const statFilter = filter || 'combined';

        // Calculate exact prediction start
        let index = 0;
        const currentDate = this.moment();
        const hours = Math.floor(currentDate.hours() / INTERVAL_MAP[mode]) * INTERVAL_MAP[mode];
        currentDate.hours(hours).minutes(0).seconds(0).milliseconds(0);

        const selectedStat = displayData.get('selectedStat');

        for (const date = this.moment(renderFrom); date.isBefore(renderTo); date.add(INTERVAL_MAP[mode], UNIT_MAP[mode])) {
            const key = date.format(KEY_FORMATTERS[mode]);
            const rawData = rawChartData[key];
            if (!rawData || selectedStat === 'feedback') break;

            let valueToCheck = null;
            if (selectedStat === 'productivity')
                valueToCheck = rawData.productivity;
            else if (calculationMode === 'meo')
                valueToCheck = rawData.meoValue;
            else if (calculationMode === 'vlh')
                valueToCheck = rawData.vlhValue;
            else // if (calculationMode === 'demand')
                valueToCheck = rawData.recommendedEmployeeCount;

            if (valueToCheck && !valueToCheck.actual && chartData.predictionStart > index)
                chartData.predictionStart = index - 1;
            if (valueToCheck && valueToCheck.actual > 0 && chartData.predictionStart < index)
                chartData.predictionStart = index;

            if (date.isSame(currentDate, UNIT_MAP[mode]))
                chartData.currentDayIndex = index;

            index += 1;
        }

        if (!chartData.currentDayIndex) chartData.currentDayIndex = currentDate.isBefore(renderTo) ? -1 : index;

        if (selectedStat && [ 'feedback', 'productivity' ].includes(selectedStat)) {
            chartData.horizontalLines = 5.25;
            chartData.lineDotSize = 5;

            let maxProductivity = 0;

            if (selectedStat === 'productivity') {
                for (const date = this.moment(renderFrom); date.isBefore(renderTo); date.add(INTERVAL_MAP[mode], UNIT_MAP[mode])) {
                    const key = date.format(KEY_FORMATTERS[mode]);
                    const stat = stats.perUnit[key];
                    if (stat && stat.productivity.combined > maxProductivity) {
                        maxProductivity = stat.productivity.combined;
                    }
                }
            }

            index = 0;

            for (const date = this.moment(renderFrom); date.isBefore(renderTo); date.add(INTERVAL_MAP[mode], UNIT_MAP[mode])) {
                const key = date.format(KEY_FORMATTERS[mode]);
                const prevKey = this.moment(date).subtract(1, UNIT_MAP[mode]).format(KEY_FORMATTERS[mode]);
                const stat = stats.perUnit[key];
                const prevStat = stats.perUnit[prevKey] || null;
                const label = date.format(DISPLAY_FORMATTERS[mode]);

                const barLabel = [ label ];

                if (stat) {
                    let value = null;
                    if (selectedStat === 'feedback') {
                        value = stat.feedbackAvg || 0;
                        barLabel.push(value.toFixed(2));
                        // value = (maxScaled - minScaled) * (value - minInp) / (maxInp - minInp) + minScaled;
                        value = 5 * ( value - 1 ) / 4 || null;

                        const arrowVal = prevStat ? ( value - 5 * ( prevStat.feedbackAvg - 1 ) / 4 ) / 4 : 0;

                        chartData.barTooltips.push({
                            title: [
                                getChartTooltipEntry({ text: date.format(DISPLAY_FORMATTERS[mode]) }, 'gray', { bold: true }),
                                getChartTooltipEntry({ arrow: arrowVal }),
                            ],
                            body: [
                                [
                                    getChartTooltipEntry({ text: 'Feedback' }, 'gray'),
                                    getChartTooltipEntry({ value: value }, 'gray', { bold: true }),
                                ],
                            ],
                        });
                    } else if (selectedStat === 'productivity') {
                        value = stat.productivity ? stat.productivity.combined || 0 : 0;
                        barLabel.push(value.toFixed(2));
                        value = 5 * value / maxProductivity;

                        const arrowVal = prevStat ? ( value - 5 * prevStat.productivity.combined / maxProductivity ) / 5 : 0;

                        chartData.barTooltips.push({
                            title: [
                                getChartTooltipEntry({ text: date.format(DISPLAY_FORMATTERS[mode]) }, 'gray', { bold: true }),
                                getChartTooltipEntry({ arrow: arrowVal }),
                            ],
                            body: [
                                [
                                    getChartTooltipEntry({ text: i18n.t('roster.view.productivity') + i18n.t('roster.comp.actualBubble') },
                                        'gray',
                                        { bold: stat.productivity.combined === stat.productivity.actual },
                                    ),
                                    getChartTooltipEntry(
                                        { value: stat.productivity.actual },
                                        'gray',
                                        { bold: stat.productivity.combined === stat.productivity.actual },
                                    ),
                                ],
                                [
                                    getChartTooltipEntry({ text: i18n.t('roster.view.productivity') + i18n.t('roster.comp.targetBubble') },
                                        'gray',
                                        { bold: stat.productivity.combined === stat.productivity.planned },
                                    ),
                                    getChartTooltipEntry(
                                        { value: stat.productivity.planned },
                                        'gray',
                                        { bold: stat.productivity.combined === stat.productivity.planned },
                                    ),
                                ],
                            ],
                        });
                    }
                    value = value ? value : ( ( mode === 'month' || selectedStat !== 'productivity' ) ? null : 0 );
                    if (selectedStat === 'feedback' && value <= 0)
                        value = null;

                    if (index < chartData.predictionStart) {
                        chartData.lineValues.push(value);
                        chartData.predictionLineValues.push(null);
                    } else if (index === chartData.predictionStart) {
                        chartData.lineValues.push(value);
                        chartData.predictionLineValues.push(value);
                    } else {
                        chartData.lineValues.push(null);
                        chartData.predictionLineValues.push(value);
                    }
                } else {
                    chartData.lineValues.push(null);
                    chartData.predictionLineValues.push(null);
                }

                chartData.barLabels.push(barLabel);

                index += 1;
            }

            return chartData;
        }

        let highestValueInChart = 1;
        let highestProductivity = 0;
        let highestFeedback = 0;

        index = 0;
        let recommendedEmployeeSum = 0;
        // let differenceSum = 0;
        for (const date = this.moment(renderFrom); date.isBefore(renderTo); date.add(INTERVAL_MAP[mode], UNIT_MAP[mode])) {
            const key = date.format(KEY_FORMATTERS[mode]);
            const rawData = rawChartData[key] || {};

            const dateTo = this.moment(date).add(INTERVAL_MAP[mode], UNIT_MAP[mode]);

            const actualShifts = rawData.actualShifts || [];
            const actualShiftValues = this.calculateRelevantShiftValue(mode, actualShifts, chartSettings, date, dateTo);
            // console.log(date.format('YYYY-MM-DD HH:mm:ss'), dateTo.format('YYYY-MM-DD HH:mm:ss'), actualShiftValues);

            const planShifts = rawData.planShifts || [];

            const assignedShifts = rawData.assignedShifts || [];
            const assignedShiftValues = this.calculateRelevantShiftValue(mode, assignedShifts, chartSettings, date, dateTo);

            const freeShifts = rawData.freeShifts || [];
            const freeShiftValues = this.calculateRelevantShiftValue(mode, freeShifts, chartSettings, date, dateTo);

            const rec = rawData.recommendedEmployeeCount || { actual: 0, planned: 0, combined: 0 };
            const meo = rawData.meoValue || { actual: 0, planned: 0, combined: 0 };
            const vlh = rawData.vlhValue || { actual: 0, planned: 0, combined: 0 };

            const curveDataMap = {
                'meo': meo,
                'vlh': vlh,
                'demand': rec,
            };
            const curveData = curveDataMap[calculationMode];

            // Render display data
            const barValues = { actual: 0, assigned: 0, overstaffed: 0, overstaffedAlt: 0, free: 0 };
            const lineValues = { actual: null, planned: null };
            let marketValues = null;

            const act = actualShiftValues;
            const ass = assignedShiftValues;
            let curveVal = (
                chartSettings.splitPredictionLine
                    ? ( curveData.actual || curveData.planned )
                    : curveData[filter]
            );

            if (curveVal)
                recommendedEmployeeSum += curveVal;

            if (!barsToDisplay.includes('actual') || index > chartData.currentDayIndex) {
                barValues.assigned = ass > curveVal ? curveVal : ass;
                barValues.overstaffed = ass > curveVal ? ass - curveVal : 0;
            } else {
                barValues.actual = act > curveVal ? curveVal : act;
                barValues.overstaffedAlt = act > curveVal ? act - curveVal : 0;
                barValues.assigned = Math.max(0, ass - act);
            }

            if (barsToDisplay.includes('free'))
                barValues.free = freeShiftValues;

            if (!linesToDisplay.includes('actual') && !linesToDisplay.includes('planned')) {
                lineValues.actual = null;
                lineValues.planned = null;
            } else if (chartSettings.splitPredictionLine) {
                lineValues.actual = linesToDisplay.includes('actual') ? curveData['actual'] : null;
                lineValues.planned = linesToDisplay.includes('planned') ? curveData['planned'] : null;
            } else if (( index < chartData.predictionStart || filter === 'actual' ) && filter !== 'planned') {
                // TODO: Check if this applies to monthHourly as well
                lineValues.actual = curveVal || curveData['actual'] || ( mode === 'month' ? null : 0 );
                lineValues.planned = null;
            } else if (( index > chartData.predictionStart || filter === 'planned' ) && filter !== 'actual') {
                lineValues.actual = null;
                // TODO: Check if this applies to monthHourly as well
                lineValues.planned = curveVal || curveData['planned'] || ( mode === 'month' ? null : 0 );
            } else if (index === chartData.predictionStart) {
                lineValues.actual = linesToDisplay.includes('actual') ? curveVal : null;
                lineValues.planned = linesToDisplay.includes('planned') ? curveVal : null;
            } else {
                lineValues.actual = null;
                lineValues.planned = null;
            }

            if (linesToDisplay.includes('market') && mode !== 'dayQuarterHourly')
                marketValues = rawData.marketEmployeeCount;

            let statValue = null;
            let statValueReadable = null;
            let barIcon = null;

            const statToDisplay = selectedStat;
            const stat = stats.perUnit[key];

            if (statToDisplay && mode !== 'monthHourly') {
                switch (statToDisplay) {
                    case 'accuracy':
                        statValue = stat.accuracy.combined;
                        // barIcon = getChartArrow(statValue - 1);
                        statValueReadable = getStatTextPercent(statValue);
                        break;
                    case 'accuracyActual':
                        statValue = stat.accuracy.actual;
                        // barIcon = getChartArrow(statValue - 1);
                        statValueReadable = getStatTextPercent(statValue);
                        break;
                    case 'accuracyPlanned':
                        statValue = stat.accuracy.planned;
                        // barIcon = getChartArrow(statValue - 1);
                        statValueReadable = getStatTextPercent(statValue);
                        break;
                    case 'overstaffing':
                        statValue = stat.overstaffing.actual;
                        // barIcon = getChartArrow(stat.overstaffingPercent[statFilter]);
                        statValueReadable = getStatText(statValue);
                        break;
                    case 'overstaffingActual':
                        statValue = stat.overstaffing.actual;
                        // barIcon = getChartArrow(stat.overstaffingPercent[statFilter]);
                        statValueReadable = getStatText(statValue);
                        break;
                    case 'overstaffingPlanned':
                        statValue = stat.overstaffing.planned;
                        // barIcon = getChartArrow(stat.overstaffingPercent[statFilter]);
                        statValueReadable = getStatText(statValue);
                        break;
                    case 'understaffing':
                        statValue = stat.understaffing.actual;
                        // barIcon = getChartArrow(-stat.understaffingPercent[statFilter]);
                        statValueReadable = getStatText(statValue);
                        break;
                    case 'understaffingActual':
                        statValue = stat.understaffing.actual;
                        // barIcon = getChartArrow(-stat.understaffingPercent[statFilter]);
                        statValueReadable = getStatText(statValue);
                        break;
                    case 'understaffingPlanned':
                        statValue = stat.understaffing.planned;
                        // barIcon = getChartArrow(-stat.understaffingPercent[statFilter]);
                        statValueReadable = getStatText(statValue);
                        break;
                    case 'prediction':
                        statValue = stat.prediction;
                        // barIcon = getChartArrow(statValue);
                        statValueReadable = getStatTextPercent(statValue);
                        break;
                    case 'utilization':
                        statValue = stat.utilization;
                        // barIcon = getChartArrow(statValue);
                        statValueReadable = getStatTextPercent(statValue);
                        break;
                    case 'coverage':
                        statValue = stat.coverage;
                        // barIcon = getChartArrow(statValue - 1);
                        statValueReadable = getStatTextPercent(statValue);
                        break;
                    case 'productivity':
                        statValue = stat.productivity[statFilter];
                        // barIcon = getChartArrow(statValue / stats.productivity[statFilter] - 1);
                        statValueReadable = getStatText(statValue);
                        break;
                    case 'feedback':
                        statValue = stat.feedbackAvg;
                        // barIcon = getChartArrow(statValue / stats.feedbackAvg - 1);
                        statValueReadable = getStatText(statValue);
                        break;
                }
            }

            // Calculate the highest element in chart to determine scaling factor for productivity and feedback lines
            const barSum = Object.values(barValues).reduce((a, b) => a + b, 0);
            if (barSum > highestValueInChart)
                highestValueInChart = barSum;
            if (lineValues.actual > highestValueInChart)
                highestValueInChart = lineValues.actual;
            if (lineValues.planned > highestValueInChart)
                highestValueInChart = lineValues.planned;
            if (marketValues && marketValues[1] > highestValueInChart)
                highestValueInChart = marketValues[1];

            if (stat.productivity[statFilter] > highestProductivity)
                highestProductivity = stat.productivity[statFilter];

            if (stat.feedbackAvg > highestFeedback)
                highestFeedback = stat.feedbackAvg;


            let barLabel = date.format(DISPLAY_FORMATTERS[mode]);
            if (mode === 'dayQuarterHourly' || mode === 'monthHourly'/* && ( index + 12 / INTERVAL_MAP[mode] ) % ( 24 / INTERVAL_MAP[mode] ) > 0*/) {
                barLabel = '';
            }
            const day = date.format('ddd');
            chartData.barLabels[index] = statValueReadable != null ? [ statValueReadable, barLabel ] : barLabel;
            if (mode === 'month' && !Array.isArray(chartData.barLabels[index])) {
                chartData.barTitles[index] = day;
            } else {
                chartData.barTitles[index] = '';
            }

            const barLabelColor = 'rgb(128,128,128)';
            chartData.barLabelColors[index] = barLabelColor;

            const indicatorValues = rawData.indicator || {};
            const actualIndicator = indicatorValues.actualTotal;
            const plannedIndicator = indicatorValues.plannedTotal;

            const indicatorData = indicatorTypeMap[chartSettings.selectedIndicatorTypeId] || {};
            const calcNames = {
                meo: i18n.t('shift.main.meo'),
                vlh: i18n.t('shift.main.vlh'),
                demand: i18n.t('roster.comp.requirement'),
            };
            const calcName = calcNames[calculationMode];
            const indicatorName = indicatorData.name || i18n.t('roster.comp.indicator');

            let assignedHoursOrShifts;
            if (index !== chartData.currentDayIndex) {
                assignedHoursOrShifts = barsToDisplay.includes('actual')
                    ? actualShiftValues || assignedShiftValues
                    : assignedShiftValues;
            } else {
                assignedHoursOrShifts = 0;
                let relevantPlanShifts = planShifts.filter(ps => ps.employeeId);
                if (barsToDisplay.includes('actual')) {
                    assignedHoursOrShifts = this.calculateRelevantShiftValue(mode, actualShifts, chartSettings, date, dateTo);
                    const employeesWithActualShifts = [];
                    for (const as of actualShifts) {
                        employeesWithActualShifts.push(as.employeeId);
                    }
                    relevantPlanShifts = relevantPlanShifts.filter(ps => !employeesWithActualShifts.includes(ps.employeeId));
                }
                // console.log(date.format(DISPLAY_FORMATTERS[mode]), assignedHoursOrShifts);
                const relPlanShiftValue = this.calculateRelevantShiftValue(mode, relevantPlanShifts, chartSettings, date, dateTo);
                assignedHoursOrShifts += relPlanShiftValue;
                // console.log(date.format(DISPLAY_FORMATTERS[mode]), assignedHoursOrShifts);
            }

            // Generate tooltips
            const displayHours = mode !== 'month' || chartSettings.displayHours;
            const hasCurveData = curveData && ( curveData.actual || curveData.planned );

            // Calculate difference between demand and shift, if actual shift, ignore planned shift with same employee id

            let demandLabel = '';
            let demandValue = null;
            let demandLabelBold = true;

            switch (filter) {
                case 'combined': {
                    demandLabel = calcName + i18n.t('roster.comp.combinedBubble');
                    demandValue = curveData.combined;
                    demandLabelBold = true;
                    break;
                }
                case 'actual': {
                    demandLabel = calcName + ' ('+ i18n.t('roster.comp.targetCaps') +')';
                    demandValue = curveData.actual;
                    demandLabelBold = true;
                    break;
                }
                case 'planned': {
                    demandLabel = calcName + i18n.t('roster.comp.targetBubbleCaps');
                    demandValue = curveData.planned;
                    break;
                }
            }

            let diffValue = demandValue ? assignedHoursOrShifts - demandValue : null;
            let diffColor = demandValue ? Math.floor(demandValue) === 0 ? 'green' : 'red' : 'gray';

            // differenceSum += assignedHoursOrShifts - (compVal || 0);

            chartData.barTooltips[index] = {
                title: [
                    getChartTooltipEntry({
                            value: actualIndicator <= 0 ? plannedIndicator : actualIndicator - plannedIndicator,
                        },
                        actualIndicator <= 0 ? 'gray' : actualIndicator > plannedIndicator ? 'green' : 'red',
                        { bold: true },
                    ),
                    ( actualIndicator != null && plannedIndicator != null )
                        ? getChartTooltipEntry({ arrow: actualIndicator - plannedIndicator <= 0 ? -stat.prediction : 0 })
                        : getChartTooltipEntry({ timesIcon: true }),
                ],
                body: [
                    [
                        getChartTooltipEntry({ text: indicatorName + ' ' + i18n.t('workBalance.summary.actual').toUpperCase() }, 'gray'),
                        getChartTooltipEntry({ value: actualIndicator }, 'gray', { bold: true }),
                    ],
                    [
                        getChartTooltipEntry({ text: indicatorName + ' ' + i18n.t('workBalance.summary.target').toUpperCase() }, 'gray'),
                        getChartTooltipEntry({ value: plannedIndicator }, 'gray', { bold: true }),
                    ],
                    [
                        getChartTooltipEntry({ text: demandLabel }, 'gray', { bold: demandLabelBold }),
                        getChartTooltipEntry({ value: demandValue }, 'gray', { bold: true }),
                    ],
                    [
                        getChartTooltipEntry({ text: displayHours ? i18n.t('roster.comp.assignedHours') : i18n.t('roster.comp.assignedShifts') }, 'gray'),
                        getChartTooltipEntry({ value: assignedHoursOrShifts }, 'gray', {
                            bold: true,
                            digits: displayHours ? 2 : 0
                        }),
                    ],
                    [
                        getChartTooltipEntry({ text: i18n.t('shift.template.deviation') }, 'gray'),
                        getChartTooltipEntry(
                            { value: diffValue },
                            hasCurveData ? diffColor : 'gray',
                            { bold: true, digits: 2 },
                        ),
                    ],
                    [
                        getChartTooltipEntry({ text: displayHours ? i18n.t('roster.comp.freeHours') : i18n.t('roster.comp.freeShifts') }, 'gray'),
                        getChartTooltipEntry({ value: freeShiftValues }, 'gray', {
                            bold: true,
                            digits: displayHours ? 2 : 0
                        }),
                    ],
                    [
                        getChartTooltipEntry({ text: i18n.t('roster.comp.marketComparison') }, 'gray'),
                        getChartTooltipEntry(
                            {
                                text: marketValues
                                    ? `${marketValues[0].toFixed(displayHours ? 2 : 0)} - ${marketValues[1].toFixed(displayHours ? 2 : 0)}${displayHours ? 'h' : ' S'}`
                                    : null,
                            },
                            'gray',
                            { bold: true },
                        ),
                    ],
                ],
            };

            chartData.orangeValues[index] = barValues.actual ? Math.max(0, barValues.actual) : null;
            chartData.greenValues[index] = barValues.assigned ? Math.max(0, barValues.assigned) : null;
            chartData.redValues[index] = barValues.overstaffed ? Math.max(0, barValues.overstaffed) : null;
            chartData.redAltValues[index] = barValues.overstaffedAlt ? Math.max(0, barValues.overstaffedAlt) : null;
            chartData.grayValues[index] = barValues.free ? Math.max(0, barValues.free) : null;

            const actLineVal = lineValues.actual !== null ? Math.max(0, lineValues.actual) : null;
            if (calculationMode === 'meo')
                chartData.blueLineValues[index] = actLineVal;
            else if (calculationMode === 'vlh')
                chartData.blueLineValues[index] = actLineVal;
            else
                chartData.lineValues[index] = actLineVal;

            chartData.predictionLineValues[index] = lineValues.planned !== null ? Math.max(0, lineValues.planned) : null;

            chartData.blueAreaValues[index] = marketValues && marketValues.length >= 2 ? marketValues : [ 0, 0 ];

            if (barIcon) {
                chartData.barIcons[index] = barIcon;
                if (mode === 'monthHourly' && ( index + 12 / INTERVAL_MAP[mode] ) % ( 24 / INTERVAL_MAP[mode] ) > 0) {
                    chartData.barIcons[index] = null;
                }
            }

            // TO DO: Add separator after day when mode === month24 // if they look good
            /*
                if (mode === 'monthHourly' && index % (24 / INTERVAL_MAP['monthHourly']) === 0) {
                  chartData.separators.push(index === 0 ? -1 : index);
                }
            */

            index += 1;
        }

        // console.log('differenceSum', differenceSum);

        // Calculate productivity and feedback curves
        index = 0;

        const productivityScale = highestProductivity / highestValueInChart;
        const feedbackScale = highestFeedback / highestValueInChart;

        for (const date = this.moment(renderFrom); date.isBefore(renderTo); date.add(INTERVAL_MAP[mode], UNIT_MAP[mode])) {
            const key = date.format(KEY_FORMATTERS[mode]);
            const stat = stats.perUnit[key];

            const productivity = stat.productivity[statFilter];
            const feedback = stat.feedbackAvg;

            if (linesToDisplay.includes('productivity'))
                chartData.yellowLineValues[index] = div(productivity, productivityScale);

            if (linesToDisplay.includes('feedback') && mode === 'month')
                chartData.redLineValues[index] = feedback ? div(feedback, feedbackScale) : null; // show only line segments where feedback !== 0 or any falsy value

            index += 1;
        }

        if (chartData.barIcons.length === 0)
            delete chartData.barIcons;

        return chartData;
    }


    /******************************************************************************/
    // Helper functions                                                           */
    /******************************************************************************/

    createShiftFromTemplate(template, date, employeeId = undefined) {
        const id = generateUUID();

        const [ fromHour, fromMinute ] = template.from.split(':');
        const shiftFrom = this.moment(date).hour(parseInt(fromHour)).minute(parseInt(fromMinute));
        const [ toHour, toMinute ] = template.to.split(':');
        const shiftTo = this.moment(date).hour(parseInt(toHour)).minute(parseInt(toMinute));

        let breakFrom = null;
        if (template.breakFrom) {
            let bf = template.breakFrom;
            if (momentOrig.isMoment(bf)) bf = bf.hours() + ':' + bf.minutes();
            let [ breakHour, breakMinute ] = bf.split(':');
            [ breakHour, breakMinute ] = [ parseInt(breakHour), parseInt(breakMinute) ];
            if (!isNaN(breakHour) && !isNaN(breakMinute))
                breakFrom = this.moment(date).hour(breakHour).minute(breakMinute);
        }

        return {
            id,

            employeeId,

            from: shiftFrom.format(),
            fromDate: shiftFrom,
            to: shiftTo.format(),
            toDate: shiftTo,
            breakValue: template.breakValue,
            breakFrom,

            templateId: template.id,
            spotId: template.spotId,
            workValue: template.workValue,
            storeId: template.storeId,
            projectId: template.projectId,
            tasks: template.tasks,
            fromLocationId: template.fromLocationId,
            toLocationId: template.toLocationId,
            published: false,
            deleted: false,
            locked: false,
            pinned: false,
            workValueMultiplier: 1,
            skillMultiplier: 1,
        };
    }

    calculateRelevantShiftValue(mode, shifts, chartSettings, from, to, logType = '') {
        from = this.moment(from);
        to = this.moment(to);

        let shiftValue = 0;

        // const logTable = [];

        for (const shift of ( shifts || [] )) {
            const shiftFrom = this.moment(shift.from);
            const shiftTo = this.moment(shift.to);

            if (mode === 'month' && !chartSettings.displayHours) {
                if (shiftFrom.isBetween(from, to, null, '[)')) shiftValue += 1;
                continue;
            }

            if (from.isSameOrAfter(shift.to) || to.isSameOrBefore(shift.from)) continue;

            const relevantFrom = shiftFrom.isBefore(from)
                ? from
                : shiftFrom;
            const relevantTo = shiftTo.isAfter(to)
                ? to
                : shiftTo;
            const relevantLength = relevantTo.diff(relevantFrom, 'hours', true);

            shiftValue += relevantLength;

            let relevantBreakVal = 0;

            // let breakLog = [];

            if (chartSettings.subtractBreaks) {
                let breaks = shift.breaks ? Object.values(shift.breaks) : null;
                if (!breaks && shift.breakFrom) {
                    const breakFrom = this.moment(shift.breakFrom);
                    const breakTo = this.moment(breakFrom).add(shift.breakValue, 'minutes');
                    breaks = [
                        { from: breakFrom, to: breakTo },
                    ];
                }

                if (breaks) {
                    for (let { from: breakFrom, to: breakTo } of breaks) {
                        breakFrom = this.moment(breakFrom);
                        breakTo = this.moment(breakTo);

                        if (breakFrom.isSameOrAfter(relevantTo) || breakTo.isSameOrBefore(relevantFrom)) continue;

                        const relevantBreakFrom = breakFrom.isBefore(relevantFrom) ? relevantFrom : breakFrom;
                        const relevantBreakTo = breakTo.isAfter(relevantTo) ? relevantTo : breakTo;

                        relevantBreakVal += relevantBreakTo.diff(relevantBreakFrom, 'hours', true);
                        // breakLog.push({
                        //     breakFrom: breakFrom.format('DD. HH:mm'),
                        //     breakTo: breakTo.format('DD. HH:mm'),
                        //     breakValue: breakTo.diff(breakFrom, 'minutes'),
                        //     relevantBreakFrom: relevantBreakFrom.format('DD. HH:mm'),
                        //     relevantBreakTo: relevantBreakTo.format('DD. HH:mm'),
                        //     relevantBreakVal,
                        // });
                    }
                } else {
                    const breakValue = shift.breakValue || 0;
                    const shiftLength = shiftTo.diff(shift.from, 'hours', true);
                    const breakPercent = ( breakValue / 60 ) / shiftLength;
                    relevantBreakVal = relevantLength * breakPercent;
                    // breakLog.push({
                    //     breakValue,
                    //     relevantBreakVal,
                    // });
                }

                shiftValue -= relevantBreakVal;
            }

            // logTable.push({
            //     shiftFrom: shiftFrom.format('DD. HH:mm'),
            //     shiftTo: shiftTo.format('DD. HH:mm'),
            //     relevantFrom: relevantFrom.format('DD. HH:mm'),
            //     relevantTo: relevantTo.format('DD. HH:mm'),
            //     relevantLength,
            // relevantBreakVal,
            // totalShiftValue: relevantLength - relevantBreakVal,
            // });

            // logTable.push(...breakLog);
        }

        // if (logType) {
        //     console.log(from.format('YYYY-MM-DD HH:mm'), '-', to.format('YYYY-MM-DD HH:mm') + ':', logType);
        //     logTable.sort((a, b) => a.fromMoment - b.fromMoment);

        // const sumObj = logTable.reduce(
        //     (sums, { totalShiftValue, relevantLength, relevantBreakVal }) => ({
        //         totalShiftValue: sums.totalShiftValue + (totalShiftValue || 0),
        //         relevantLength: sums.relevantLength + (relevantLength || 0),
        //         relevantBreakVal: sums.relevantBreakVal + (relevantBreakVal || 0),
        //     }),
        //     { totalShiftValue: 0, relevantLength: 0, relevantBreakVal: 0 }
        // );
        //
        // logTable.push(sumObj);

        // console.table(logTable.map((entry) => {
        //     delete entry.fromMoment;
        //     return entry;
        // }));
        // }

        return shiftValue;
    }

}


/**
 *
 * @param content {{text?: string, icon?: string, arrow?: number, timesIcon?: boolean, value?: number | null, percent?: number | null}}
 * @param color {'red'|'green'|'gray'}
 * @param options {{rotation?: number, fontSize?: string, bold?: boolean, digits?: number}}
 * @returns {{}}
 */
function getChartTooltipEntry(content = {}, color = 'gray', options = {}) {
    options = {
        rotation: 0,
        fontSize: '10pt',
        bold: false,
        digits: 2,
        ...options,
    };
    const colorMap = {
        red: 'var(--col-error)',
        green: '#6d8d23',
        gray: '#808080',
    };

    let args = { color: colorMap[color] };

    if (content.text) {
        args.text = content.text;
        args.font = options.fontSize + ' ' + ( options.bold ? 'DistrictProBold' : 'DistrictProBook' );
    } else if (content.value !== null && !isNaN(content.value)) {
        args.text = content.value.toFixed(options.digits);
        args.font = options.fontSize + ' ' + ( options.bold ? 'DistrictProBold' : 'DistrictProBook' );
    } else if (content.percent !== null && !isNaN(content.percent)) {
        args.text = ( content.percent * 100 ).toFixed(options.digits) + '%';
        args.font = options.fontSize + ' ' + ( options.bold ? 'DistrictProBold' : 'DistrictProBook' );
    } else if (content.icon) {
        args.icon = {
            icon: content.icon,
            rotation: options.rotation + 'deg',
            color: colorMap[color],
        };
    } else if (!isNaN(content.arrow)) {
        args.icon = getChartArrow(content.arrow);
    } else if (content.timesIcon) {
        args.icon = getChartTimesIcon();
    } else {
        args.text = '--';
        args.font = options.fontSize + ' ' + ( options.bold ? 'DistrictProBold' : 'DistrictProBook' );
    }

    return args;
}

export function getChartArrow(value) {
    value = isNaN(value) ? 0 : value;
    value = Math.max(-1, Math.min(1, value));
    const p = value / 2;
    const a = p * Math.PI;
    const cos = Math.cos(a);
    const sin = Math.sin(a);
    const phi = Math.atan2(cos, sin);
    const icon = 'fas fa-arrow-circle-up';
    let color;
    const pPercent = Math.abs(value);
    if (pPercent > 0.5) {
        color = colorBetween(( 1 - pPercent ) * 2, [ 255, 193, 7 ], [ 220, 53, 69 ]);
    } else {
        color = colorBetween(pPercent * 2, [ 255, 193, 7 ], [ 109, 141, 33 ]);
    }
    return { icon, color: `rgb(${color.join(',')})`, rotation: `${phi}rad` };
}

export function getChartTimesIcon() {
    return {
        icon: 'far fa-times-circle',
        color: '#808080',
    };
}

/**
 * Returns a color between start and end color based on a value between 0 and 1
 * @param p {number}
 * @param rgbStart {[number,number,number]}
 * @param rgbEnd {[number,number,number]}
 * @returns {[number,number,number]}
 */
export function colorBetween(p, rgbStart, rgbEnd) {
    const w = p * 2 - 1;
    const w1 = ( w + 1 ) / 2.0;
    const w2 = 1 - w1;

    return [
        Math.round(rgbStart[0] * w1 + rgbEnd[0] * w2),
        Math.round(rgbStart[1] * w1 + rgbEnd[1] * w2),
        Math.round(rgbStart[2] * w1 + rgbEnd[2] * w2),
    ];
}

function getStatText(statValue, postfix = '') {
    return isNaN(statValue)
        ? '--'
        : `${Math.round(statValue) + postfix}`;
}

function getStatTextPercent(statValue) {
    return getStatText(statValue * 100, '%');
}

function generateUUID() {
    let d = new Date().getTime();
    let d2 = ( performance && performance.now && ( performance.now() * 1000 ) ) || 0;
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        let r = Math.random() * 16;
        if (d > 0) {
            r = ( d + r ) % 16 | 0;
            d = Math.floor(d / 16);
        } else {
            r = ( d2 + r ) % 16 | 0;
            d2 = Math.floor(d2 / 16);
        }
        return ( c == 'x' ? r : ( r & 0x7 | 0x8 ) ).toString(16);
    });
}

/**
 * Creates a key that can be used to cache the data in vuex
 * @param keyData {{storeId: string|number, mode: string, templateView: boolean, date: string}}
 */
export function createCachingKey(keyData) {
    return objectHash(keyData);
}


export default PersonnelDeploymentHelpers;
